import { FormEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  DRAFT_LAB_LEAGUE_SETUP_API_PREFIX,
  DRAFT_LAB_CANONICAL_TEAM_NAMES,
  DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_HEADER,
  DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_TTL_SECONDS,
  DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH,
  DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH,
  emptyDraftLabLeagueSetupConfig,
  type DraftLabLeagueSetupConfig,
} from "../shared/draft-lab-league-setup";
import { isKeeperEligibilityPlayer } from "../shared/keeper-eligibility-player";
import type { DraftSnapshot, KeeperPlayer, LeagueSummary, SpecialKeeperCandidate, WithdrawOptions } from "../shared/types";
import { createLatestRequestCoordinator, orderDraftTeamStatuses, scrollContainerToBottom } from "./draft-room-ui";
import { createReconnectingDraftSocket } from "./draft-room-socket";
import { createClientId } from "./client-id";
import { retryAfterSecondsFromHeader } from "./http-retry-after";
import {
  parseLegacyDraftLabAuth,
  verifyLegacyDraftLabPassword,
  type LegacyDraftLabAuth,
} from "./draft-lab-legacy-migration";
import {
  captureEmailVerificationController,
  leagueHubBrowserEntryPath,
} from "./league-hub-browser-route";
import { matchesPositionFilter, POSITION_FILTER_ORDER } from "./draft-position-filter";
import { draftLabKeeperDraftFits } from "./keeper-draft-fit-engine";
import {
  formatBackupFileSize,
  inspectLeagueBackupFile,
  leagueRestoreFailureGuidance,
  type LeagueBackupPreview,
  type LeagueBackupVariant,
} from "./league-backup";
import { createKeeperDraftSoundSession, type KeeperDraftSoundSession } from "./keeper-draft-sound-session";
import { draftLabAutopopulatePlayerIds } from "./keeper-autopopulate-engine";
import { autoPopulateKeeperSlots, keeperOptionsForSlot, keeperSlotsAreDirty, updateKeeperSlot } from "./keeper-selection";
import {
  StandardDraftTimePollDialog,
  StandardDraftTimePollEditor,
  useStandardDraftTimePoll,
  type StandardDraftPollController,
} from "./standard-draft-time-poll";
import "./styles.css";

// Verification tokens are bearer credentials. Capture and scrub them during
// module evaluation, before React mounts or any application fetch can start.
// The controller keeps the raw value inside its private closure; React can
// observe only a sanitized route kind and invoke an attempt.
const emailVerificationController = captureEmailVerificationController(
  window.location,
  window.history.replaceState.bind(window.history),
  confirmEmailVerificationToken,
);

type Page = "home" | "keepers" | "settings" | "draft" | "rosters";

function returnBrowserHistoryToLeagueHub(): void {
  window.history.replaceState({}, "", leagueHubBrowserEntryPath(window.location));
}

function initialPageFromLocation(): Page {
  const path = window.location.pathname.toLocaleLowerCase("en-US");
  if (path.endsWith("/rosters") || path.endsWith("/rosters/")) return "rosters";
  return path.startsWith("/keeper-draft") ? "draft" : "home";
}
type AuthView = "login" | "register" | "forgot" | "reset" | "verify";
type EmailVerificationState = "inbox" | "confirming" | "invalid" | "transient";
type AdminTab = "overview" | "assignments" | "teams" | "eligibles" | "schedule" | "accounts" | "draftLab" | "reset";

interface SessionInfo {
  user: {
    id: string;
    email: string;
    username: string;
    mustChangePassword: boolean;
  };
  membership: {
    id: string;
    leagueId: string;
    role: "commissioner" | "manager";
  };
  team: { id: string; key: string; name: string } | null;
  idleExpiresAt: number;
  absoluteExpiresAt: number;
}

interface KeeperState {
  team: { id: string; key?: string; name: string };
  submission: { id: string; status: string; revision: number; submitted_at: number | null };
  keeperLimit: number;
  editable: boolean;
  eligibles: Array<KeeperPlayer & {
    keeperCostRound?: number | null;
    keeperCostOverallPick?: number | null;
  }>;
}

interface AdminUser {
  id: string;
  email: string;
  username: string;
  status: string;
  verified: boolean;
  role: string;
  teamId: string | null;
  teamName: string | null;
}

interface AdminTeam {
  id: string;
  name: string;
  key: string;
  displayOrder: number;
  userId: string | null;
  managerName: string | null;
  submissionStatus: string;
  keeperCount: number;
}

interface AdminOverview {
  users: AdminUser[];
  teams: AdminTeam[];
  settings: {
    phase: string;
    season: number;
    keeper_limit?: number;
    keeper_selection_deadline_at: number | null;
    standard_draft_at: number | null;
    sleeper_invite_url?: string;
    sleeper_league_url?: string;
    timezone?: string;
  };
  outbox: Array<Record<string, unknown>>;
}

interface DraftSnapshotView extends DraftSnapshot {
  currentPickNumber: number;
  maxKeepers: number;
  completionReason: string | null;
  serverTime: number;
}

interface DraftTurnOrderEntry {
  planPosition: number;
  teamId: string;
  teamName: string;
  status: "pending" | "on_clock" | "completed";
  pickNumber: number | null;
}

interface KeeperDraftFitView {
  rankedPlayerIds: string[];
  roundedFitByPlayerId: Record<string, number>;
}

type WithdrawalKeeperOption = SpecialKeeperCandidate;

interface RosterPlayerView {
  playerId: string;
  name: string;
  nflTeam: string;
  positions: string[];
  byeWeek: number | null;
  slot: number;
  source: "keeper_selection" | "keeper_draft";
  pickNumber: number | null;
}

interface TeamRosterView {
  teamId: string;
  teamKey: string;
  teamName: string;
  managerName: string | null;
  displayOrder: number;
  withdrawn: boolean;
  keeperCount: number;
  keepers: RosterPlayerView[];
}

interface LeagueRostersView {
  season: number;
  keeperLimit: number;
  teams: TeamRosterView[];
}

function submissionStatusLabel(status: string): string {
  if (status === "draft") return "Choosing";
  return status ? `${status.charAt(0).toLocaleUpperCase("en-US")}${status.slice(1)}` : "Choosing";
}

function pillLabel(label: string): string {
  return label
    .replaceAll("_", " ")
    .replace(/\b\p{L}/gu, (letter) => letter.toLocaleUpperCase("en-US"));
}

interface PickPreparation {
  requiresConfirmation: boolean;
  confirmationToken: string;
  expiresAt: number;
  expectedVersion: number;
  expectedPickNumber: number;
  summary: {
    teamId: string;
    teamName: string;
    playerId: string;
    playerName: string;
    warning: string;
  };
}

interface ToastMessage {
  id: string;
  message: string;
  kind: "success" | "error";
}

class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function csrfToken(): string | undefined {
  const match = document.cookie.split("; ").find((part) => part.startsWith("tb_csrf="));
  return match ? decodeURIComponent(match.slice("tb_csrf=".length)) : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!new Set(["GET", "HEAD", "OPTIONS"]).has(method)) {
    const token = csrfToken();
    if (token) headers.set("X-CSRF-Token", token);
  }

  const response = await fetch(path, { ...init, headers, credentials: "include" });
  const raw: unknown = response.status === 204 ? undefined : await response.json().catch(() => undefined);
  if (!response.ok) {
    const envelope = isRecord(raw) && isRecord(raw.error) ? raw.error : raw;
    const code = isRecord(envelope) && typeof envelope.code === "string" ? envelope.code : "request_failed";
    const message = isRecord(envelope) && typeof envelope.message === "string"
      ? envelope.message
      : `Request failed (${response.status}).`;
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new ApiError(
      response.status,
      code,
      message,
      Number.isFinite(retryAfter) && retryAfter >= 0 ? Math.ceil(retryAfter) : null,
    );
  }
  if (isRecord(raw) && raw.ok === true && "data" in raw) return raw.data as T;
  return raw as T;
}

async function confirmEmailVerificationToken(token: string): Promise<void> {
  const response = await fetch("/api/auth/verify-email", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
    credentials: "omit",
    cache: "no-store",
    // The token has already been removed from browser history. same-origin
    // keeps both Origin and Referrer confined to this exact site so the public
    // mutation can satisfy the server's same-origin proof without leaking the
    // bearer credential to another origin.
    referrerPolicy: "same-origin",
    mode: "same-origin",
    redirect: "error",
  });
  const raw: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const envelope = isRecord(raw) && isRecord(raw.error) ? raw.error : raw;
    const code = isRecord(envelope) && typeof envelope.code === "string"
      ? envelope.code
      : "request_failed";
    const message = isRecord(envelope) && typeof envelope.message === "string"
      ? envelope.message
      : `Request failed (${response.status}).`;
    throw new ApiError(
      response.status,
      code,
      message,
      retryAfterSecondsFromHeader(response.headers.get("Retry-After")),
    );
  }
  if (!(isRecord(raw) && raw.ok === true && isRecord(raw.data) && raw.data.verified === true)) {
    throw new ApiError(
      response.status,
      "invalid_verification_response",
      "Team Beer returned an invalid email-confirmation response. Please try again.",
    );
  }
}

async function apiBlob(path: string, init: RequestInit): Promise<{ blob: Blob; disposition: string }> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) headers.set("Content-Type", "application/json");
  if (!new Set(["GET", "HEAD", "OPTIONS"]).has(method)) {
    const token = csrfToken();
    if (token) headers.set("X-CSRF-Token", token);
  }

  const response = await fetch(path, { ...init, headers, credentials: "include" });
  if (!response.ok) {
    const raw: unknown = await response.json().catch(() => undefined);
    const envelope = isRecord(raw) && isRecord(raw.error) ? raw.error : raw;
    const code = isRecord(envelope) && typeof envelope.code === "string" ? envelope.code : "request_failed";
    const message = isRecord(envelope) && typeof envelope.message === "string"
      ? envelope.message
      : `Request failed (${response.status}).`;
    const retryAfter = Number(response.headers.get("Retry-After"));
    throw new ApiError(
      response.status,
      code,
      message,
      Number.isFinite(retryAfter) && retryAfter >= 0 ? Math.ceil(retryAfter) : null,
    );
  }
  return {
    blob: await response.blob(),
    disposition: response.headers.get("Content-Disposition") ?? "",
  };
}

function backupDownloadFileName(disposition: string, variant: LeagueBackupVariant): string {
  const headerName = disposition.match(/filename="([^"\\/]+)"/iu)?.[1] ?? "";
  const safeName = headerName.replace(/[^A-Za-z0-9._-]/gu, "-").slice(0, 180);
  return safeName.toLocaleLowerCase("en-US").endsWith(".json")
    ? safeName
    : `team-beer-${variant}-backup.json`;
}

function saveBackupBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function backupPollBallotCount(preview: LeagueBackupPreview): number | null {
  const value = (preview.summary as unknown as Record<string, unknown>).pollBallotCount;
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

function externalLinkLabel(value: string): string {
  return value.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

function normalizeAdminOverview(raw: unknown): AdminOverview {
  const source = isRecord(raw) ? raw : {};
  const users = Array.isArray(source.users) ? source.users : [];
  const teams = Array.isArray(source.teams) ? source.teams : [];
  const settings = isRecord(source.settings) ? source.settings : {};
  return {
    users: users.filter(isRecord).map((user) => ({
      id: asString(user.id),
      email: asString(user.email),
      username: asString(user.username),
      status: asString(user.status, "pending_email"),
      verified: Boolean(user.verified ?? user.email_verified_at),
      role: asString(user.role, "manager"),
      teamId: asNullableString(user.teamId ?? user.team_id),
      teamName: asNullableString(user.teamName ?? user.team_name),
    })),
    teams: teams.filter(isRecord).map((team) => ({
      id: asString(team.id),
      name: asString(team.name),
      key: asString(team.key ?? team.team_key),
      displayOrder: asNumber(team.displayOrder ?? team.display_order),
      userId: asNullableString(team.userId ?? team.user_id),
      managerName: asNullableString(team.managerName ?? team.manager_name),
      submissionStatus: asString(team.submissionStatus ?? team.submission_status, "draft"),
      keeperCount: asNumber(team.keeperCount ?? team.keeper_count),
    })),
    settings: {
      phase: asString(settings.phase, "keeper_selection"),
      season: asNumber(settings.season, 2026),
      keeper_limit: asNumber(settings.keeper_limit, 10),
      keeper_selection_deadline_at: settings.keeper_selection_deadline_at == null ? null : asNumber(settings.keeper_selection_deadline_at),
      standard_draft_at: settings.standard_draft_at == null ? null : asNumber(settings.standard_draft_at),
      sleeper_invite_url: asString(settings.sleeper_invite_url),
      sleeper_league_url: asString(settings.sleeper_league_url),
      timezone: asString(settings.timezone, "America/Los_Angeles"),
    },
    outbox: Array.isArray(source.outbox) ? source.outbox.filter(isRecord) : [],
  };
}

function normalizeLeagueRosters(raw: unknown): LeagueRostersView {
  const source = isRecord(raw) ? raw : {};
  const rosterRows = Array.isArray(source.rosters)
    ? source.rosters
    : Array.isArray(source.teams)
      ? source.teams
      : [];
  const teams = rosterRows.filter(isRecord).map((team, teamIndex) => {
    const playerRows = Array.isArray(team.players)
      ? team.players
      : Array.isArray(team.keepers)
        ? team.keepers
        : Array.isArray(team.selectedKeepers)
          ? team.selectedKeepers
          : [];
    const keepers = playerRows.filter(isRecord).map((player, playerIndex): RosterPlayerView => {
      const rawPositions = Array.isArray(player.positions) ? player.positions : [];
      const rawSource = asString(player.source, "keeper_selection");
      return {
        playerId: asString(player.playerId ?? player.player_id ?? player.id, `roster-player-${teamIndex}-${playerIndex}`),
        name: asString(player.name, "Unknown player"),
        nflTeam: asString(player.nflTeam ?? player.nfl_team, "—"),
        positions: rawPositions.filter((position): position is string => typeof position === "string" && position.length > 0),
        byeWeek: player.byeWeek == null && player.bye_week == null ? null : asNumber(player.byeWeek ?? player.bye_week),
        slot: asNumber(player.slot ?? player.keeperSlot ?? player.keeper_slot, playerIndex + 1),
        source: rawSource === "keeper_draft" ? "keeper_draft" : "keeper_selection",
        pickNumber: player.pickNumber == null && player.pick_number == null ? null : asNumber(player.pickNumber ?? player.pick_number),
      };
    }).sort((left, right) => left.slot - right.slot || (left.pickNumber ?? 0) - (right.pickNumber ?? 0) || left.name.localeCompare(right.name));
    return {
      teamId: asString(team.teamId ?? team.team_id ?? team.id, `roster-team-${teamIndex}`),
      teamKey: asString(team.teamKey ?? team.team_key ?? team.key),
      teamName: asString(team.teamName ?? team.team_name ?? team.name, "Unknown team"),
      managerName: asNullableString(team.managerName ?? team.manager_name),
      displayOrder: asNumber(team.displayOrder ?? team.display_order, teamIndex),
      withdrawn: Boolean(team.withdrawn),
      keeperCount: asNumber(team.keeperCount ?? team.keeper_count, keepers.length),
      keepers,
    };
  }).sort((left, right) => left.displayOrder - right.displayOrder || left.teamName.localeCompare(right.teamName));
  return {
    season: asNumber(source.season, 2026),
    keeperLimit: asNumber(source.keeperLimit ?? source.keeper_limit, Math.max(0, ...teams.map((team) => team.keeperCount))),
    teams,
  };
}

function readableDate(value: number | null): string {
  if (!value) return "Not scheduled yet";
  const milliseconds = value < 10_000_000_000 ? value * 1000 : value;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "full",
    timeStyle: "short",
    timeZone: "America/Los_Angeles",
  }).format(new Date(milliseconds));
}

function shortTime(value: number): string {
  const milliseconds = value < 10_000_000_000 ? value * 1000 : value;
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(new Date(milliseconds));
}

function localDateTimeValue(value: number | null): string {
  if (!value) return "";
  const date = new Date(value < 10_000_000_000 ? value * 1000 : value);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

const PASSWORD_REQUIREMENTS = "Use at least 8 characters with at least one letter and one number. Special characters are allowed.";

function meetsPasswordRequirements(password: string): boolean {
  return Array.from(password).length >= 8 && /\p{L}/u.test(password) && /\p{N}/u.test(password);
}

function futureStep(summary: LeagueSummary): string {
  if (summary.keeperDraftComplete) {
    return `The Keeper Draft is complete. Your next step is the Standard Draft on Sleeper${summary.standardDraftAt ? ` on ${readableDate(summary.standardDraftAt)}` : "; its date has not been finalized"}.`;
  }
  if (summary.selectedCount < summary.keeperLimit) {
    if (summary.phase === "keeper_draft_lobby") {
      return "The Keeper Draft lobby is open. Enter the Keeper Draft to join the other managers and wait for the commissioner to start.";
    }
    if (summary.phase === "keeper_draft_active") {
      return "The Keeper Draft is in progress. Enter the Keeper Draft to make selections when your team is on the clock.";
    }
    if (summary.phase === "keeper_selection_locked") {
      return "Keeper submissions are locked while the live Keeper Draft is prepared.";
    }
    if (summary.submissionStatus === "draft" && summary.keeperSelections.length > 0) {
      return "Your keeper selections are saved, but they have not been submitted. Submit them to lock them in.";
    }
    return "Once all teams submit, return here to participate in the live Keeper Draft. Your team will manually make every selection.";
  }
  if (summary.submissionStatus === "draft") {
    return "Your keeper selections are saved, but they have not been submitted. Submit them to lock them in.";
  }
  return `You have filled all 10 keeper spots. Your next step is the Standard Draft on Sleeper${summary.standardDraftAt ? ` on ${readableDate(summary.standardDraftAt)}` : "; its date has not been finalized"}.`;
}

function ButtonSpinner() {
  return <span className="spinner spinner--small" aria-hidden="true" />;
}

function LoadingScreen({ label = "Loading Team Beer…" }: { label?: string }) {
  return (
    <div className="loading-screen" role="status">
      <span className="spinner" aria-hidden="true" />
      <strong>{label}</strong>
    </div>
  );
}

function Brand({ onHome }: { onHome?: () => void }) {
  return (
    <button className="brand brand-button" type="button" onClick={onHome} aria-label="Team Beer home">
      <img className="brand-logo" src="/team-beer-logo.png" alt="" />
      <span className="brand-copy">
        <span className="brand-title">Team Beer</span>
        <span className="brand-subtitle">League Hub · 2026</span>
      </span>
    </button>
  );
}

function SignOutIcon() {
  return (
    <svg className="sign-out-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M10 5H5v14h5M14 8l4 4-4 4M8 12h10" />
    </svg>
  );
}

function Header({ session, page, pollRankedCount, pollComplete, onOpenPoll, onNavigate, onLogout }: {
  session: SessionInfo;
  page: Page;
  pollRankedCount: number;
  pollComplete: boolean;
  onOpenPoll: () => void;
  onNavigate: (page: Page) => void;
  onLogout: () => void;
}) {
  const commissioner = session.membership.role === "commissioner";
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  return (
    <>
      <header className="app-header">
        <Brand onHome={() => onNavigate("home")} />
        <nav className="app-header-actions" aria-label="Account navigation">
          <span className="user-chip" title={session.user.email}>
            <span aria-hidden="true">●</span>
            <strong>{session.team?.name ?? session.user.username}</strong>
          </span>
          <button id="standard-draft-time-poll-trigger" aria-label={`Draft time poll, ${pollRankedCount} of 10 ranked${pollComplete ? ", saved and complete" : ""}`} className={`button button--quiet poll-header-button ${pollComplete ? "poll-header-button--complete" : ""}`} type="button" onClick={onOpenPoll}>
            <span className="compact-button-icon" aria-hidden="true">☷</span>
            <span className="button-label">Draft time poll</span>
            <span className="poll-header-button__count" aria-hidden="true">{pollRankedCount}/10</span>
          </button>
          {commissioner && (
            <button aria-label="League Settings" className={`button button--quiet ${page === "settings" ? "is-active" : ""}`} type="button" onClick={() => onNavigate("settings")}>
              <span className="compact-button-icon" aria-hidden="true">⚙</span>
              <span className="button-label">League Settings</span>
            </button>
          )}
          <button aria-label="Sign out" className="button button--quiet sign-out-button" type="button" onClick={() => setConfirmingSignOut(true)}>
            <span className="compact-button-icon"><SignOutIcon /></span>
            <span className="button-label">Sign out</span>
          </button>
        </nav>
      </header>
      <ConfirmDialog
        open={confirmingSignOut}
        title="Sign out or return to your team page?"
        cancelLabel="Return to team page"
        confirmLabel="Sign out"
        danger
        onClose={() => setConfirmingSignOut(false)}
        onCancel={() => { setConfirmingSignOut(false); onNavigate("home"); }}
        onConfirm={() => { setConfirmingSignOut(false); onLogout(); }}
      >
        <p>Choose whether to stay signed in and return to your team page, or end this session and sign out.</p>
        {page === "keepers" && <div className="alert alert--warning">Any keeper changes that have not been saved will be discarded if you sign out.</div>}
      </ConfirmDialog>
    </>
  );
}

const LOCAL_ACCOUNTS = Array.from({ length: 12 }, (_, index) => {
  const number = String(index + 1).padStart(2, "0");
  return { username: `manager${number}` };
});

function AuthShell({ view, onView, onAuthenticated, onRegistered, onLoginVerificationRequired, verificationIdentifier, verificationState, verificationRetryAvailableAt, verificationSucceeded, onRetryVerification, notify }: {
  view: AuthView;
  onView: (view: AuthView) => void;
  onAuthenticated: () => Promise<void>;
  onRegistered: (identifier: string) => void;
  onLoginVerificationRequired: (identifier: string) => void;
  verificationIdentifier: string;
  verificationState: EmailVerificationState;
  verificationRetryAvailableAt: number | null;
  verificationSucceeded: boolean;
  onRetryVerification: () => void;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  return (
    <div className="app-shell">
      <header className="app-header"><Brand /></header>
      <main className="page-shell">
        <div className="page page--auth">
          <div className="auth-layout">
            <section className="auth-intro">
              <p className="eyebrow">Team Beer · 2026</p>
              <h1>Team Beer keeper management</h1>
              <p>Register, submit keeper selections, review league status, and participate in the live Keeper Draft.</p>
              <div className="notice notice--info auth-note">
                <span aria-hidden="true">↗</span>
                <span>The Standard Draft will be held on Sleeper. Draft Lab remains available separately for mock drafts.</span>
              </div>
            </section>
            {view === "login" && <LoginForm onView={onView} onAuthenticated={onAuthenticated} onVerificationRequired={onLoginVerificationRequired} verificationSucceeded={verificationSucceeded} notify={notify} />}
            {view === "register" && <RegisterForm onView={onView} onRegistered={onRegistered} notify={notify} />}
            {view === "forgot" && <ForgotForm onView={onView} notify={notify} />}
            {view === "reset" && <ResetForm onView={onView} notify={notify} />}
            {view === "verify" && <VerifyCard onView={onView} initialIdentifier={verificationIdentifier} state={verificationState} retryAvailableAt={verificationRetryAvailableAt} onRetry={onRetryVerification} />}
          </div>
        </div>
      </main>
    </div>
  );
}

function LoginForm({ onView, onAuthenticated, onVerificationRequired, verificationSucceeded, notify }: {
  onView: (view: AuthView) => void;
  onAuthenticated: () => Promise<void>;
  onVerificationRequired: (identifier: string) => void;
  verificationSucceeded: boolean;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const verificationNoticeRef = useRef<HTMLDivElement>(null);
  const localQaAccounts = /^(localhost|127\.0\.0\.1|192\.168\.)/.test(window.location.hostname)
    && new URLSearchParams(window.location.search).get("qaAccounts") === "1";

  useEffect(() => {
    if (!verificationSucceeded) return;
    const frame = window.requestAnimationFrame(() => verificationNoticeRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [verificationSucceeded]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await api("/api/auth/login", { method: "POST", body: JSON.stringify({ identifier, password }) });
      await onAuthenticated();
      notify("Signed in.");
    } catch (error) {
      if (error instanceof ApiError && error.code === "email_verification_required") {
        onVerificationRequired(identifier.trim());
        return;
      }
      notify(error instanceof Error ? error.message : "Sign-in failed.", "error");
    } finally {
      setBusy(false);
    }
  }

  function selectTest(value: string) {
    const account = LOCAL_ACCOUNTS.find((candidate) => candidate.username === value);
    if (!account) return;
    setIdentifier(account.username);
    setPassword("");
  }

  return (
    <section className={`auth-card${verificationSucceeded ? " auth-card--verification-succeeded" : ""}`} aria-labelledby="login-heading">
      <p className="eyebrow">Manager sign in</p>
      <h2 id="login-heading">Sign in</h2>
      <p className="auth-card__login-instructions">Use your confirmed email address or username.</p>
      {verificationSucceeded && (
        <div className="alert alert--success auth-card__verification-success" ref={verificationNoticeRef} role="status" tabIndex={-1}>
          Your email has been verified. You can log in now.
        </div>
      )}
      <form className="form-stack" onSubmit={submit}>
        {localQaAccounts && (
          <label className="field">
            <span>Local test account</span>
            <select defaultValue="" onChange={(event) => selectTest(event.target.value)}>
              <option value="">Choose one of 12 test managers</option>
              {LOCAL_ACCOUNTS.map((account) => <option key={account.username} value={account.username}>{account.username}</option>)}
            </select>
            <small className="field-hint">Localhost only. Selecting an account fills its username; use the private local test-account sheet for its password.</small>
          </label>
        )}
        <label className="field">
          <span>Email or username</span>
          <input autoComplete="username" required value={identifier} onChange={(event) => setIdentifier(event.target.value)} />
        </label>
        <label className="field">
          <span>Password</span>
          <input type="password" autoComplete="current-password" required value={password} onChange={(event) => setPassword(event.target.value)} />
        </label>
        <button className="button button--primary button--wide" disabled={busy} type="submit">{busy && <ButtonSpinner />}Sign in</button>
      </form>
      <div className="auth-switch"><button className="text-button" type="button" onClick={() => onView("forgot")}>Forgot password?</button></div>
      <div className="auth-switch">Need an account? <button className="text-button" type="button" onClick={() => onView("register")}>Register</button></div>
    </section>
  );
}

function RegisterForm({ onView, onRegistered, notify }: {
  onView: (view: AuthView) => void;
  onRegistered: (identifier: string) => void;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    const confirmation = String(form.get("confirmation") ?? "");
    if (password !== confirmation) {
      notify("Passwords do not match.", "error");
      return;
    }
    if (!meetsPasswordRequirements(password)) {
      notify(PASSWORD_REQUIREMENTS, "error");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/register", {
        method: "POST",
        body: JSON.stringify({
          username: String(form.get("username") ?? ""),
          email: String(form.get("email") ?? ""),
          password,
        }),
      });
      notify("Registration received. Check your email to confirm your account.");
      onRegistered(String(form.get("email") ?? form.get("username") ?? ""));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Registration failed.", "error");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="auth-card" aria-labelledby="register-heading">
      <p className="eyebrow">Create account</p>
      <h2 id="register-heading">Create your account</h2>
      <p>Confirm your email before the commissioner assigns your team.</p>
      <form className="form-stack" onSubmit={submit}>
        <label className="field"><span>Username</span><input name="username" autoComplete="username" minLength={3} required /></label>
        <label className="field"><span>Email</span><input name="email" type="email" autoComplete="email" required /></label>
        <label className="field"><span>Password</span><input name="password" type="password" autoComplete="new-password" minLength={8} required /><small className="field-hint">{PASSWORD_REQUIREMENTS}</small></label>
        <label className="field"><span>Confirm password</span><input name="confirmation" type="password" autoComplete="new-password" minLength={8} required /></label>
        <button className="button button--primary button--wide" disabled={busy} type="submit">{busy && <ButtonSpinner />}Create account</button>
      </form>
      <div className="auth-switch">Already registered? <button className="text-button" type="button" onClick={() => onView("login")}>Sign in</button></div>
    </section>
  );
}

function ForgotForm({ onView, notify }: {
  onView: (view: AuthView) => void;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const identifier = String(new FormData(event.currentTarget).get("identifier") ?? "");
    setBusy(true);
    try {
      await api("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ identifier }) });
      notify("If that account exists, a password-reset email is on its way.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to request a reset.", "error");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="auth-card">
      <p className="eyebrow">Account recovery</p><h2>Reset your password</h2>
      <p>Enter your email or username. We never expose your current password.</p>
      <form className="form-stack" onSubmit={submit}>
        <label className="field"><span>Email or username</span><input name="identifier" autoComplete="username" required /></label>
        <button className="button button--primary button--wide" disabled={busy} type="submit">{busy && <ButtonSpinner />}Send reset link</button>
      </form>
      <div className="auth-switch"><button className="text-button" type="button" onClick={() => onView("login")}>Back to sign in</button></div>
    </section>
  );
}

function ResetForm({ onView, notify }: {
  onView: (view: AuthView) => void;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const token = new URLSearchParams(window.location.search).get("token") ?? "";
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const password = String(form.get("password") ?? "");
    if (password !== String(form.get("confirmation") ?? "")) {
      notify("Passwords do not match.", "error");
      return;
    }
    if (!meetsPasswordRequirements(password)) {
      notify(PASSWORD_REQUIREMENTS, "error");
      return;
    }
    setBusy(true);
    try {
      await api("/api/auth/reset-password", { method: "POST", body: JSON.stringify({ token, password }) });
      returnBrowserHistoryToLeagueHub();
      notify("Password updated. Sign in with your new password.");
      onView("login");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to reset password.", "error");
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="auth-card">
      <p className="eyebrow">Password reset</p><h2>Choose a new password</h2>
      <p>This link can be used only once.</p>
      {!token && <div className="alert alert--danger">This reset link is missing its token.</div>}
      <form className="form-stack" onSubmit={submit}>
        <label className="field"><span>New password</span><input name="password" type="password" autoComplete="new-password" minLength={8} required /><small className="field-hint">{PASSWORD_REQUIREMENTS}</small></label>
        <label className="field"><span>Confirm password</span><input name="confirmation" type="password" autoComplete="new-password" minLength={8} required /></label>
        <button className="button button--primary button--wide" disabled={busy || !token} type="submit">{busy && <ButtonSpinner />}Update password</button>
      </form>
      <div className="auth-switch"><button className="text-button" type="button" onClick={() => onView("login")}>Back to sign in</button></div>
    </section>
  );
}

function VerifyCard({ onView, initialIdentifier, state, retryAvailableAt, onRetry }: {
  onView: (view: AuthView) => void;
  initialIdentifier: string;
  state: EmailVerificationState;
  retryAvailableAt: number | null;
  onRetry: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [resendFeedback, setResendFeedback] = useState<{ kind: "success" | "error"; message: string } | null>(null);
  const [retryClock, setRetryClock] = useState(() => Date.now());
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    setResendFeedback(null);
    const frame = window.requestAnimationFrame(() => headingRef.current?.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [state]);

  useEffect(() => {
    const now = Date.now();
    setRetryClock(now);
    if (state !== "transient" || retryAvailableAt === null || retryAvailableAt <= now) return;
    const timer = window.setTimeout(() => setRetryClock(Date.now()), retryAvailableAt - now);
    return () => window.clearTimeout(timer);
  }, [retryAvailableAt, state]);

  async function resend(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const identifier = String(new FormData(event.currentTarget).get("identifier") ?? "");
    setBusy(true);
    setResendFeedback(null);
    try {
      await api("/api/auth/resend-verification", {
        method: "POST",
        body: JSON.stringify({ identifier }),
      });
      setResendFeedback({
        kind: "success",
        message: "If that account still needs confirmation, a new link is on its way.",
      });
    } catch (error) {
      setResendFeedback({
        kind: "error",
        message: error instanceof Error ? error.message : "Unable to resend the confirmation link.",
      });
    } finally {
      setBusy(false);
    }
  }

  const heading = state === "confirming"
    ? "Confirming your email…"
    : state === "invalid"
      ? "This confirmation link can't be used"
      : state === "transient"
        ? "We couldn't confirm your email"
        : "Check your inbox";
  const showResend = state === "inbox" || state === "invalid";
  const rateLimited = state === "transient" && retryAvailableAt !== null;
  const retrySecondsRemaining = rateLimited
    ? Math.max(0, Math.ceil((retryAvailableAt - retryClock) / 1_000))
    : 0;
  const retryCoolingDown = retrySecondsRemaining > 0;

  return (
    <section className="auth-card auth-card--verify" aria-labelledby="email-verification-heading" aria-busy={state === "confirming"}>
      <p className="eyebrow">Email confirmation</p>
      <h2 id="email-verification-heading" ref={headingRef} tabIndex={-1}>{heading}</h2>
      {state === "inbox" && <p>Open the confirmation link we sent. After confirmation, the commissioner can assign your account to a team.</p>}
      {state === "confirming" && <div className="alert alert--info" role="status"><ButtonSpinner /><span>Keep this page open while Team Beer confirms your account.</span></div>}
      {state === "invalid" && <div className="alert alert--danger" role="alert"><span aria-hidden="true">!</span><span>This link is invalid, expired, or has already been used. If you already confirmed your email, try signing in. Otherwise, request a new link.</span></div>}
      {state === "transient" && <div className="alert alert--danger" role="alert"><span aria-hidden="true">!</span><span>{rateLimited
        ? retryCoolingDown
          ? `Too many confirmation attempts reached the site at once. Please wait ${retrySecondsRemaining} ${retrySecondsRemaining === 1 ? "second" : "seconds"}, then try again. Your link is still kept only in this page.`
          : "The confirmation cooldown has ended. You can try again now. Your link is still kept only in this page."
        : "A temporary connection or server problem interrupted confirmation. Your link has been kept only in this page so you can try again."}</span></div>}
      {state === "transient" && <button className="button button--primary button--wide" disabled={retryCoolingDown} type="button" onClick={onRetry}>Try again</button>}
      {showResend && <form className="form-stack" onSubmit={resend}>
          <label className="field">
            <span>Need a new confirmation link?</span>
            <input name="identifier" autoComplete="username" placeholder="Email or username" defaultValue={initialIdentifier} required />
          </label>
          <button className="button button--secondary button--wide" disabled={busy} type="submit">
            {busy && <ButtonSpinner />}Resend confirmation email
          </button>
        </form>}
      {resendFeedback && <div className={`alert alert--${resendFeedback.kind === "success" ? "success" : "danger"}`} role={resendFeedback.kind === "success" ? "status" : "alert"}>{resendFeedback.message}</div>}
      {state !== "confirming" && <button className="button button--quiet button--wide" type="button" onClick={() => onView("login")}>Return to sign in</button>}
    </section>
  );
}

function Dashboard({ summary, session, pollController, onNavigate, onRefresh, notify }: {
  summary: LeagueSummary;
  session: SessionInfo;
  pollController: StandardDraftPollController;
  onNavigate: (page: Page) => void;
  onRefresh: () => Promise<void>;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [confirmingSubmission, setConfirmingSubmission] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const progress = Math.min(100, Math.max(0, (summary.teamsSubmitted / Math.max(1, summary.teamsSubmitted + summary.teamsRemaining)) * 100));
  const canEnterDraft = ["keeper_draft_lobby", "keeper_draft_active"].includes(summary.phase)
    && !summary.keeperDraftComplete;
  const noKeeperDraftNeeded = summary.phase === "standard_draft_pending" && !summary.keeperDraftRequired;
  const beforeKeeperDraft = ["registration", "keeper_selection", "keeper_selection_locked", "keeper_draft_lobby", "standard_draft_pending"].includes(summary.phase);
  const canEditKeeperSelections = summary.submissionStatus === "draft" && ["registration", "keeper_selection"].includes(summary.phase);
  const hasSavedUnsubmittedSelections = summary.submissionStatus === "draft" && summary.keeperSelections.length > 0;
  const finalKeeperSelections = summary.selectedKeepers?.length
    ? summary.selectedKeepers
    : summary.keeperSelections;
  const nextStepTone = !summary.submitted
    ? "status-banner--warning"
    : summary.keeperDraftComplete || summary.selectedCount >= summary.keeperLimit
      ? "status-banner--success"
      : "status-banner--info";

  async function submitFromTeamPage() {
    setSubmitting(true);
    try {
      await api("/api/keepers/submit", { method: "POST", body: "{}" });
      setConfirmingSubmission(false);
      notify("Your keeper selections are locked in.");
      await onRefresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to submit keepers.", "error");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page">
      <div className="dashboard-grid">
        <div className="dashboard-main">
          <section className="hero-card">
            <p className="eyebrow">{summary.teamName ?? "Team assignment pending"}</p>
            <h1>{summary.keeperDraftComplete ? "Keeper Draft complete" : noKeeperDraftNeeded ? "Keeper selections complete" : summary.submitted ? "Keeper selections submitted" : hasSavedUnsubmittedSelections ? "Submit your keeper selections" : "Select your keepers"}</h1>
            <p>{summary.teamName ? futureStep(summary) : "Your account is confirmed. The commissioner will assign it to a team before keeper selection opens."}</p>
            <div className="hero-actions">
              {summary.teamName && !summary.submitted && <button className="button button--secondary" type="button" onClick={() => onNavigate("keepers")}>{hasSavedUnsubmittedSelections ? "Edit keepers" : "Choose keepers"}</button>}
              {hasSavedUnsubmittedSelections && canEditKeeperSelections && <button className="button button--primary" type="button" onClick={() => setConfirmingSubmission(true)}>Submit selections</button>}
              {canEnterDraft && <button className="button button--primary" type="button" onClick={() => onNavigate("draft")}>Enter Keeper Draft</button>}
              {summary.keeperDraftComplete && <button className="button button--primary" type="button" onClick={() => onNavigate("rosters")}>View league rosters</button>}
              {summary.keeperDraftComplete && <button className="button button--secondary" type="button" onClick={() => onNavigate("draft")}>Review results</button>}
              {session.membership.role === "commissioner" && <button className="button button--quiet" type="button" onClick={() => onNavigate("settings")}>League Settings</button>}
            </div>
          </section>

          {summary.teamName && beforeKeeperDraft && <section className="surface-card keeper-choice-summary">
            <div className="card-header">
              <div>
                <h2>Your keeper selections</h2>
                <p className="card-header-subtitle">{summary.keeperSelections.length} of {summary.keeperLimit} selected</p>
              </div>
              <div className="keeper-choice-summary__actions">
                <span className={`status-pill ${summary.submitted ? "status-pill--success" : "status-pill--pending"}`}>{summary.submitted ? "Submitted" : "Saved Draft"}</span>
                {canEditKeeperSelections && <button className="button button--secondary button--small" type="button" onClick={() => onNavigate("keepers")}>Edit choices</button>}
              </div>
            </div>
            <div className="card-body stack">
              {hasSavedUnsubmittedSelections && <div className="status-banner status-banner--warning" role="status"><span aria-hidden="true">!</span><span>Your keeper selections are saved, but they have not been submitted yet.</span></div>}
              {summary.keeperSelections.length > 0 ? <ol className="keeper-choice-list">
                {summary.keeperSelections.map((player) => <li className="keeper-choice-item" key={player.playerId}>
                  <span className="keeper-choice-slot">{player.slot}</span>
                  <span className="keeper-choice-player"><strong>{player.name}</strong><small>{player.nflTeam} · {player.positions.join("/") || "—"}{player.byeWeek ? ` · BYE ${player.byeWeek}` : ""}</small></span>
                </li>)}
              </ol> : <div className="keeper-choice-empty"><p>No keeper choices have been saved.</p></div>}
            </div>
          </section>}

          {!summary.keeperDraftComplete && <section className="surface-card">
            <div className="card-header"><h2>Keeper submission status</h2><span className="status-pill status-pill--info">Current</span></div>
            <div className="card-body stack">
              <div className="metric-grid">
                <div className="metric-card metric-card--aqua"><span className="metric-label">Submitted</span><strong className="metric-value">{summary.teamsSubmitted}</strong></div>
                <div className="metric-card metric-card--amber"><span className="metric-label">Still choosing</span><strong className="metric-value">{summary.teamsRemaining}</strong></div>
                <div className="metric-card metric-card--lime"><span className="metric-label">Your keepers</span><strong className="metric-value">{summary.selectedCount}</strong></div>
                <div className="metric-card"><span className="metric-label">Keeper limit</span><strong className="metric-value">{summary.keeperLimit}</strong></div>
              </div>
              <div className="progress-track" role="progressbar" aria-label="Teams submitted" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}><span className="progress-bar" style={{ width: `${progress}%` }} /></div>
              <p className="muted">{summary.teamsRemaining === 0 ? "Every team has submitted." : `${summary.teamsRemaining} ${summary.teamsRemaining === 1 ? "team still needs" : "teams still need"} to submit.`}</p>
            </div>
          </section>}

          {summary.keeperDraftComplete && summary.teamName && <section className="surface-card selected-keepers-panel">
            <div className="card-header"><div><h2>Selected keepers</h2><p className="card-header-subtitle">Your final keeper roster</p></div><span className="status-pill status-pill--success">Keepers: {finalKeeperSelections.length}</span></div>
            <div className="card-body">
              {finalKeeperSelections.length > 0 ? <ol className="keeper-choice-list">
                {finalKeeperSelections.map((player) => <li className="keeper-choice-item" key={player.playerId}>
                  <span className="keeper-choice-slot">{player.slot}</span>
                  <span className="keeper-choice-player"><strong>{player.name}</strong><small>{player.nflTeam} · {player.positions.join("/") || "—"}{player.byeWeek ? ` · BYE ${player.byeWeek}` : ""}</small></span>
                </li>)}
              </ol> : <div className="keeper-choice-empty"><p>No keepers were selected.</p></div>}
            </div>
          </section>}

          <section className="surface-card">
            <div className="card-header"><h2>Next step</h2></div>
            <div className="card-body stack">
              <div className={`status-banner ${nextStepTone}`}>
                <span aria-hidden="true">{!summary.submitted ? "!" : summary.keeperDraftComplete || summary.selectedCount >= summary.keeperLimit ? "✓" : "→"}</span><span>{futureStep(summary)}</span>
              </div>
              <p className="muted">Standard Draft: <strong>{readableDate(summary.standardDraftAt)}</strong> · Time shown in Pacific Time.</p>
            </div>
          </section>

          <section className="surface-card standard-draft-order-card">
            <div className="card-header standard-draft-order-header">
              <h2>Standard Draft order</h2>
            </div>
            <div className="card-body">
              <ol className="standard-draft-order-list" aria-label="Standard Draft order">
                {summary.standardDraftOrder.map((entry) => {
                  const isCurrentTeam = session.team?.id === entry.teamId;
                  return <li
                    className={`standard-draft-order-item${isCurrentTeam ? " standard-draft-order-item--current" : ""}`}
                    key={entry.teamId}
                    aria-current={isCurrentTeam ? "true" : undefined}
                  >
                    <span className="standard-draft-order-position" aria-hidden="true">{String(entry.position).padStart(2, "0")}</span>
                    <span className="standard-draft-order-team">{entry.teamName}</span>
                    {isCurrentTeam && <span className="standard-draft-order-you">Your team</span>}
                  </li>;
                })}
              </ol>
            </div>
          </section>
        </div>

        <aside className="dashboard-sidebar" aria-label="League links">
          <section className="surface-card">
            <div className="card-header"><h2>Sleeper league</h2></div>
            <div className="card-body stack">
              <p>Team Beer uses Sleeper for the Standard Draft. Accept the invitation and review the league page before draft day.</p>
              <a className="link-card" href={summary.sleeperInviteUrl} target="_blank" rel="noreferrer"><span><strong>Accept Sleeper invite</strong><small>League invitation</small></span><span aria-hidden="true">↗</span></a>
              <a className="link-card" href={summary.sleeperLeagueUrl} target="_blank" rel="noreferrer"><span><strong>Open league page</strong><small>League 1362151667696406528</small></span><span aria-hidden="true">↗</span></a>
              <p className="field-hint">The Standard Draft will be held on Sleeper for now.</p>
            </div>
          </section>
          <div id="standard-draft-time-poll-inline" className="dashboard-sidebar__poll">
            <StandardDraftTimePollEditor controller={pollController} />
          </div>
          <section className="surface-card">
            <div className="card-header"><h2>Draft Lab</h2></div>
            <div className="card-body stack">
              <p>Use Draft Lab to practice Team Beer keeper and standard-draft scenarios. Practice results do not change this league.</p>
              <a className="link-card" href={summary.keeperDraftComplete && summary.draftLabHandoffAvailable && summary.draftLabHandoffUrl ? summary.draftLabHandoffUrl : summary.draftLabUrl} target="_blank" rel="noreferrer"><span><strong>{summary.keeperDraftComplete && summary.draftLabHandoffAvailable ? "Continue in Draft Lab" : "Open Draft Lab"}</strong><small>{externalLinkLabel(summary.draftLabUrl)}</small></span><span aria-hidden="true">↗</span></a>
            </div>
          </section>
        </aside>
      </div>
      <ConfirmDialog open={confirmingSubmission} title="Submit keeper selections?" cancelLabel="Return to team page" confirmLabel="Submit selections" busy={submitting} onCancel={() => setConfirmingSubmission(false)} onConfirm={() => void submitFromTeamPage()}>
        <p>You selected <strong>{summary.keeperSelections.length}</strong> of {summary.keeperLimit} keepers. Submission locks these choices and sends any open spots to the live Keeper Draft.</p>
      </ConfirmDialog>
    </div>
  );
}

function RostersPage({ currentTeamId, onReturn }: {
  currentTeamId: string | null;
  onReturn: () => void;
}) {
  const [rosters, setRosters] = useState<LeagueRostersView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loadRosters = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api<unknown>("/api/league/rosters");
      const normalized = normalizeLeagueRosters(response);
      if (normalized.teams.length === 0) throw new Error("No completed team rosters are available yet.");
      setRosters(normalized);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Unable to load league rosters.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRosters();
  }, [loadRosters]);

  return (
    <div className="page rosters-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Keeper Draft complete</p>
          <h1>League rosters</h1>
          <p>Review every team’s final keepers for the {rosters?.season ?? 2026} season.</p>
        </div>
        <div className="page-heading-actions">
          <button className="button button--secondary" type="button" onClick={onReturn}>Return to team page</button>
        </div>
      </div>

      {loading && <section className="surface-card roster-page-state" role="status" aria-live="polite"><span className="spinner" aria-hidden="true" /><strong>Loading league rosters…</strong></section>}
      {!loading && error && <section className="surface-card roster-page-state" role="alert"><div><h2>Rosters are unavailable</h2><p>{error}</p></div><button className="button button--secondary" type="button" onClick={() => void loadRosters()}>Try again</button></section>}
      {!loading && rosters && <section aria-labelledby="league-rosters-heading">
        <div className="roster-section-heading">
          <div><h2 id="league-rosters-heading">Final keeper rosters</h2><p>{rosters.teams.length} teams · Up to {rosters.keeperLimit} keepers per team</p></div>
          <span className="status-pill status-pill--success">Teams: {rosters.teams.length}</span>
        </div>
        <div className="roster-grid">
          {rosters.teams.map((team) => {
            const isCurrentTeam = team.teamId === currentTeamId;
            return <article className={`surface-card roster-card ${isCurrentTeam ? "is-current-team" : ""}`} key={team.teamId} aria-labelledby={`roster-${team.teamId}`}>
              <div className="card-header roster-card-header">
                <div className="roster-team-copy">
                  <h2 id={`roster-${team.teamId}`}>{team.teamName}</h2>
                  {team.managerName && <p className="card-header-subtitle">Manager: {team.managerName}</p>}
                </div>
                <div className="roster-card-badges">
                  {isCurrentTeam && <span className="status-pill status-pill--info">Your Team</span>}
                  {team.withdrawn && <span className="status-pill status-pill--pending">Withdrawn</span>}
                  <span className="status-pill status-pill--success">Keepers: {team.keeperCount}</span>
                </div>
              </div>
              <div className="card-body roster-card-body">
                {team.keepers.length > 0 ? <ol className="roster-player-list">
                  {team.keepers.map((player) => {
                    const primaryPosition = player.positions[0] ?? "—";
                    const sourceLabel = player.source === "keeper_draft"
                      ? `Keeper Draft${player.pickNumber ? ` · Pick ${player.pickNumber}` : ""}`
                      : "Keeper Selection";
                    return <li className="roster-player-row" key={`${team.teamId}-${player.playerId}`}>
                      <span className="roster-player-slot" aria-label={`Keeper slot ${player.slot}`}>{player.slot}</span>
                      <span className="roster-player-copy">
                        <strong>{player.name}</strong>
                        <small>{player.nflTeam} · {player.positions.join("/") || "—"}{player.byeWeek ? ` · Bye ${player.byeWeek}` : ""}</small>
                        <small className="roster-player-source">{sourceLabel}</small>
                      </span>
                      <span className="position-badge roster-position-badge" data-position={primaryPosition}>{primaryPosition}</span>
                    </li>;
                  })}
                </ol> : <div className="keeper-choice-empty"><p>No keepers were selected.</p></div>}
              </div>
            </article>;
          })}
        </div>
      </section>}
    </div>
  );
}

function CloseButton({ onClick, disabled = false }: { onClick: () => void; disabled?: boolean }) {
  return (
    <button className="dialog-close" type="button" aria-label="Close" onClick={onClick} disabled={disabled}>
      <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
    </button>
  );
}

function ConfirmDialog({ open, title, children, cancelLabel = "Cancel", confirmLabel, danger = false, busy = false, confirmDisabled = false, onClose, onCancel, onConfirm }: {
  open: boolean;
  title: string;
  children: React.ReactNode;
  cancelLabel?: string;
  confirmLabel: string;
  danger?: boolean;
  busy?: boolean;
  confirmDisabled?: boolean;
  onClose?: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const bodyId = useId();
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  const close = () => {
    if (!busy) (onClose ?? onCancel)();
  };
  const cancel = () => {
    if (!busy) onCancel();
  };
  return (
    <dialog ref={ref} className="modal" aria-labelledby={titleId} aria-describedby={bodyId} onCancel={(event) => { event.preventDefault(); close(); }}>
      <div className="modal-card" aria-busy={busy}>
        <div className="modal-header"><h2 id={titleId}>{title}</h2><CloseButton onClick={close} disabled={busy} /></div>
        <div className="modal-body" id={bodyId}>{children}</div>
        <div className="modal-actions">
          <button className="button button--quiet" type="button" onClick={cancel} disabled={busy}>{cancelLabel}</button>
          <button className={`button ${danger ? "button--danger" : "button--primary"}`} type="button" onClick={onConfirm} disabled={busy || confirmDisabled}>{busy && <ButtonSpinner />}{confirmLabel}</button>
        </div>
      </div>
    </dialog>
  );
}

function slotsFromKeeperState(state: KeeperState): Array<string | null> {
  const selected = state.eligibles
    .filter((player) => player.selected && isKeeperEligibilityPlayer(player))
    .sort((left, right) => (left.slot ?? 999) - (right.slot ?? 999));
  return Array.from({ length: state.keeperLimit }, (_, index) => selected[index]?.eligibleId ?? null);
}

function KeeperSelection({ notify, onDone, onReturn, onDirtyChange }: {
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  onDone: () => Promise<void>;
  onReturn: () => void;
  onDirtyChange: (dirty: boolean) => void;
}) {
  const [state, setState] = useState<KeeperState | null>(null);
  const [slots, setSlots] = useState<Array<string | null>>([]);
  const [savedSlots, setSavedSlots] = useState<Array<string | null>>([]);
  const [busy, setBusy] = useState(false);
  const [autoPopulating, setAutoPopulating] = useState(false);
  const autoPopulateLockRef = useRef(false);
  const [confirming, setConfirming] = useState(false);

  const load = useCallback(async () => {
    try {
      const next = await api<KeeperState>("/api/keepers");
      setState(next);
      const nextSlots = slotsFromKeeperState(next);
      setSlots(nextSlots);
      setSavedSlots(nextSlots);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to load keepers.", "error");
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);
  const dirty = keeperSlotsAreDirty(slots, savedSlots);
  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [dirty]);

  function updateSlot(index: number, eligibleId: string) {
    setSlots((current) => updateKeeperSlot(current, index, eligibleId || null));
  }

  async function save(showToast = true): Promise<boolean> {
    setBusy(true);
    try {
      const keepers = slots.filter((value): value is string => Boolean(value)).map((eligibleId) => ({ eligibleId }));
      const next = await api<KeeperState>("/api/keepers", { method: "PUT", body: JSON.stringify({ keepers }) });
      setState(next);
      const nextSlots = slotsFromKeeperState(next);
      setSlots(nextSlots);
      setSavedSlots(nextSlots);
      if (showToast) notify("Keeper choices saved.");
      return true;
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to save keepers.", "error");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    if (!await save(false)) return;
    setBusy(true);
    try {
      await api("/api/keepers/submit", { method: "POST", body: "{}" });
      setConfirming(false);
      notify("Your keeper selections are locked in.");
      await onDone();
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to submit keepers.", "error");
    } finally {
      setBusy(false);
    }
  }

  async function autoPopulate() {
    if (!state || busy || autoPopulating || autoPopulateLockRef.current) return;
    // React commits the visual disabled state after this handler returns. Keep
    // a synchronous lock as well so a rapid double activation cannot launch
    // two expensive recommendation workers against the same form state.
    autoPopulateLockRef.current = true;
    setAutoPopulating(true);
    const selectableEligibles = state.eligibles.filter(isKeeperEligibilityPlayer);
    const eligibleById = new Map(
      selectableEligibles.map((eligible) => [eligible.eligibleId, eligible]),
    );
    const eligibleIdByPlayerId = new Map(
      selectableEligibles.map((eligible) => [eligible.playerId, eligible.eligibleId]),
    );
    const preservedPlayerIds = slots
      .map((eligibleId) => eligibleId ? eligibleById.get(eligibleId)?.playerId : null)
      .filter((playerId): playerId is string => Boolean(playerId));

    try {
      const rankedPlayerIds = await draftLabAutopopulatePlayerIds(
        state.team.name,
        selectableEligibles.map((eligible) => eligible.playerId),
        preservedPlayerIds,
        state.keeperLimit,
      );
      const rankedEligibleIds = rankedPlayerIds
        .map((playerId) => eligibleIdByPlayerId.get(playerId))
        .filter((eligibleId): eligibleId is string => Boolean(eligibleId));
      setSlots((current) => autoPopulateKeeperSlots(
        selectableEligibles,
        current,
        rankedEligibleIds,
        state.keeperLimit,
      ));
      notify("Keeper slots auto-populated using Draft Lab values. Review them before submitting.");
    } catch {
      notify(
        "Draft Lab recommendations were unavailable. No keeper selections were changed. Try Auto-populate again or choose keepers manually.",
        "error",
      );
    } finally {
      autoPopulateLockRef.current = false;
      setAutoPopulating(false);
    }
  }

  if (!state) return <LoadingScreen label="Loading eligible keepers…" />;
  const selectedCount = slots.filter(Boolean).length;
  const selectableEligibles = state.eligibles.filter(isKeeperEligibilityPlayer);
  const autoPopulateTarget = Math.min(state.keeperLimit, selectableEligibles.length);
  const byId = new Map(selectableEligibles.map((player) => [player.eligibleId, player]));
  const controlsBusy = busy || autoPopulating;
  return (
    <div className="page page--narrow keeper-selection">
      <div className="page-heading"><div><p className="eyebrow">Initial keeper selections</p><h1>Choose up to {state.keeperLimit} keepers</h1><p>Your latest owned standard-draft picks are used first. Empty slots send your team into the live Keeper Draft.</p></div><div className="page-heading-actions"><button className="button button--secondary" type="button" onClick={onReturn}>Return to team page</button></div></div>
      {!state.editable && <div className="status-banner status-banner--success"><span>✓</span><span>Your submission is locked. These choices can no longer be edited.</span></div>}
      <section className="surface-card">
        <div className="card-header"><h2>{state.team.name}</h2><span className="status-pill status-pill--info">Selected: {selectedCount}</span></div>
        <div className="card-body keeper-grid">
          {slots.map((eligibleId, index) => {
            const player = eligibleId ? byId.get(eligibleId) : undefined;
            return (
              <label className={`keeper-slot ${eligibleId ? "is-filled" : ""}`} key={index}>
                <span className="keeper-slot-header"><span className="keeper-slot-title">Keeper {index + 1}</span><span className="keeper-cost">{player?.keeperCostRound ? `R${player.keeperCostRound}` : player ? "Selected" : "Open"}</span></span>
                <select disabled={!state.editable || controlsBusy} value={eligibleId ?? ""} onChange={(event) => updateSlot(index, event.target.value)}>
                  <option value="">Open slot</option>
                  {keeperOptionsForSlot(selectableEligibles, slots, index).map((candidate) => <option key={candidate.eligibleId} value={candidate.eligibleId}>{candidate.name} · {candidate.positions.join("/")} · {candidate.nflTeam}</option>)}
                </select>
              </label>
            );
          })}
        </div>
      </section>
      <div className="keeper-summary">
        <div className="summary-values"><span className="keeper-count-summary"><span className="metric-label">Selected</span><strong className="summary-value">{selectedCount}/{state.keeperLimit}</strong></span><span className="summary-message">{selectedCount < state.keeperLimit ? `${state.keeperLimit - selectedCount} Keeper Draft ${state.keeperLimit - selectedCount === 1 ? "pick" : "picks"}` : "Keeper Draft not required"}</span></div>
        {state.editable && <div className="button-row"><button className="button button--quiet" type="button" disabled={controlsBusy || selectedCount >= autoPopulateTarget} onClick={() => void autoPopulate()}>{autoPopulating && <ButtonSpinner />}Auto-populate</button><button className="button button--quiet" type="button" disabled={controlsBusy || selectedCount === 0} onClick={() => setSlots(Array(state.keeperLimit).fill(null))}>Clear</button><button className="button button--secondary" type="button" disabled={controlsBusy} onClick={() => void save()}>{busy && <ButtonSpinner />}Save</button><button className="button button--primary" type="button" disabled={controlsBusy} onClick={() => setConfirming(true)}>Submit keepers</button></div>}
      </div>
      <ConfirmDialog open={confirming} title="Lock in keeper selections?" confirmLabel="Submit and lock" busy={busy} onCancel={() => setConfirming(false)} onConfirm={() => void submit()}>
        <p>You selected <strong>{selectedCount}</strong> of {state.keeperLimit} keepers. After submission, these choices cannot be edited.</p>
        {selectedCount < state.keeperLimit && <div className="alert alert--warning">Your team will receive {state.keeperLimit - selectedCount} selection{state.keeperLimit - selectedCount === 1 ? "" : "s"} in the live Keeper Draft.</div>}
      </ConfirmDialog>
    </div>
  );
}

function LeagueSettings({ notify, onDraft, onReturn, onRefresh, onPollReset }: {
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  onDraft: () => void;
  onReturn: () => void;
  onRefresh: () => Promise<void>;
  onPollReset: () => Promise<void>;
}) {
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [tab, setTab] = useState<AdminTab>("overview");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setOverview(normalizeAdminOverview(await api<unknown>("/api/admin/overview")));
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to load League Settings.", "error");
    }
  }, [notify]);
  useEffect(() => { void load(); }, [load]);

  async function assign(userId: string, teamId: string) {
    setBusy(true);
    try {
      const result = await api<{ alreadyAssigned: boolean; message: string | null }>(
        "/api/admin/assign",
        { method: "POST", body: JSON.stringify({ userId, teamId }) },
      );
      if (result.alreadyAssigned) {
        notify(result.message ?? "Manager has already been assigned to this team.", "error");
      } else {
        notify("Team assignment saved. An email notification was queued.");
      }
      // The admin overview, signed-in session, and manager dashboard are
      // separate client caches. Refresh them together so assigning the
      // currently signed-in commissioner takes effect without a hard reload.
      await Promise.all([load(), onRefresh()]);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to assign team.", "error");
    } finally { setBusy(false); }
  }

  async function renameTeam(teamId: string, name: string) {
    setBusy(true);
    try {
      const result = await api<{ teamId: string; name: string; renamed: boolean }>(
        `/api/admin/teams/${encodeURIComponent(teamId)}`,
        { method: "PATCH", body: JSON.stringify({ name }) },
      );
      notify(result.renamed ? `${result.name} is now the team name.` : "That team name is already saved.");
      await Promise.all([load(), onRefresh()]);
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to change the team name.", "error");
      throw error;
    } finally {
      setBusy(false);
    }
  }

  async function unsubmitTeamKeepers(teamId: string) {
    setBusy(true);
    try {
      await api(`/api/admin/teams/${encodeURIComponent(teamId)}/unsubmit-keepers`, {
        method: "POST",
        body: "{}",
      });
      await Promise.all([load(), onRefresh()]);
      notify("Keeper selections reopened. The existing choices were preserved for the manager to edit and resubmit.");
    } catch (error) {
      notify(error instanceof Error ? error.message : "Unable to reopen keeper selections.", "error");
      throw error;
    } finally {
      setBusy(false);
    }
  }

  async function initializeDraft() {
    setBusy(true);
    try {
      await api("/api/admin/draft/initialize", { method: "POST", body: "{}" });
      notify("Keeper Draft lobby initialized.");
      onDraft();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to initialize draft.", "error"); }
    finally { setBusy(false); }
  }

  async function startDraft() {
    setBusy(true);
    try {
      await api("/api/admin/draft/start", { method: "POST", body: "{}" });
      notify("Keeper Draft started.");
      onDraft();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to start draft.", "error"); }
    finally { setBusy(false); }
  }

  async function resetLeague(password: string, idempotencyKey: string) {
    setBusy(true);
    try {
      await api("/api/admin/reset-league", {
        method: "POST",
        body: JSON.stringify({ password, confirmation: "RESET LEAGUE", idempotencyKey }),
      });
      await Promise.all([load(), onRefresh(), onPollReset()]);
      setTab("overview");
      notify("League reset complete. Your commissioner account is the only registration remaining.");
    } finally {
      setBusy(false);
    }
  }

  async function resetProgress(password: string, idempotencyKey: string) {
    setBusy(true);
    try {
      await api("/api/admin/reset-progress", {
        method: "POST",
        body: JSON.stringify({ password, confirmation: "RESET PROGRESS", idempotencyKey }),
      });
      await Promise.all([load(), onRefresh()]);
      setTab("overview");
      notify("Keeper selections and league progress were reset. Registrations and team assignments were preserved.");
    } finally {
      setBusy(false);
    }
  }

  async function resetStandardDraftPoll(accountPassword: string, idempotencyKey: string) {
    setBusy(true);
    try {
      await api("/api/admin/standard-draft-time-poll/reset", {
        method: "POST",
        body: JSON.stringify({ accountPassword, confirmation: "RESET POLL", idempotencyKey }),
      });
      await onPollReset();
      notify("Every Standard Draft time ranking was deleted. Registrations and league progress were preserved.");
    } finally {
      setBusy(false);
    }
  }

  async function downloadLeagueBackup(variant: LeagueBackupVariant, password: string) {
    setBusy(true);
    try {
      const download = await apiBlob("/api/admin/league-backup", {
        method: "POST",
        body: JSON.stringify({ variant, password }),
      });
      saveBackupBlob(download.blob, backupDownloadFileName(download.disposition, variant));
      notify(variant === "full" ? "Full league backup downloaded." : "Registrations-only backup downloaded.");
    } finally {
      setBusy(false);
    }
  }

  async function restoreLeagueBackup(preview: LeagueBackupPreview, password: string, idempotencyKey: string) {
    setBusy(true);
    try {
      const result = await api<{ restored: boolean; variant: LeagueBackupVariant; signInRequired: boolean }>("/api/admin/league-restore", {
        method: "POST",
        body: JSON.stringify({
          confirmation: "RESTORE LEAGUE BACKUP",
          password,
          backup: preview.backup,
          idempotencyKey,
        }),
      });
      notify(result.variant === "full"
        ? "Full league backup restored. Sign in again to continue."
        : "Registrations restored. Sign in again to continue.");
      window.setTimeout(() => void onRefresh(), 0);
    } finally {
      setBusy(false);
    }
  }

  if (!overview) return <LoadingScreen label="Opening League Settings…" />;
  const submitted = overview.teams.filter((team) => ["submitted", "locked"].includes(team.submissionStatus)).length;
  return (
    <div className="page">
      <div className="page-heading"><div><p className="eyebrow">Commissioner controls</p><h1>League Settings</h1><p>Manage team assignments, eligible players, accounts, schedules, and the live Keeper Draft.</p></div><button className="button button--secondary" type="button" onClick={onReturn}>Return to team page</button></div>
      <div className="league-settings-layout">
        <nav className="settings-nav surface-card" aria-label="League Settings sections">
          {(["overview", "assignments", "teams", "eligibles", "schedule", "accounts", "draftLab", "reset"] as AdminTab[]).map((item) => <button key={item} className={tab === item ? "is-active" : ""} type="button" onClick={(event) => { setTab(item); event.currentTarget.scrollIntoView({ inline: "nearest", block: "nearest" }); }}>{item === "overview" ? "Overview" : item === "assignments" ? "Team assignments" : item === "teams" ? "Team names" : item === "eligibles" ? "Eligible players" : item === "schedule" ? "Schedule & draft" : item === "accounts" ? "Accounts" : item === "draftLab" ? "Draft Lab access" : "Reset league"}</button>)}
        </nav>
        <div className="settings-content">
          {tab === "overview" && (
            <>
              <section className="metric-grid">
                <div className="metric-card metric-card--aqua"><span className="metric-label">Registered</span><strong className="metric-value">{overview.users.length}</strong></div>
                <div className="metric-card metric-card--lime"><span className="metric-label">Verified</span><strong className="metric-value">{overview.users.filter((user) => user.verified).length}</strong></div>
                <div className="metric-card metric-card--amber"><span className="metric-label">Submitted</span><strong className="metric-value">{submitted}</strong></div>
                <div className="metric-card"><span className="metric-label">Remaining</span><strong className="metric-value">{Math.max(0, overview.teams.length - submitted)}</strong></div>
              </section>
              <section className="surface-card"><div className="card-header"><h2>League status</h2><span className="status-pill status-pill--info">{pillLabel(overview.settings.phase)}</span></div><div className="card-body"><TeamStatusTable teams={overview.teams} /></div></section>
              <section className="surface-card"><div className="card-header"><h2>Email delivery</h2></div><div className="card-body stack"><div className="notice notice--warning"><span>!</span><span>Production messages are sent to each account&apos;s registered email address. Local development messages remain redirected to the commissioner safety inbox.</span></div><p className="muted">Recent queued messages: {overview.outbox.length}</p></div></section>
            </>
          )}
          {tab === "assignments" && <AssignmentPanel users={overview.users} teams={overview.teams} busy={busy} onAssign={assign} />}
          {tab === "teams" && <TeamNamesPanel teams={overview.teams} phase={overview.settings.phase} busy={busy} onRename={renameTeam} onUnsubmit={unsubmitTeamKeepers} />}
          {tab === "eligibles" && <EligibilityPanel teams={overview.teams} notify={notify} />}
          {tab === "schedule" && <SchedulePanel overview={overview} busy={busy} notify={notify} reload={load} onInitialize={() => void initializeDraft()} onStart={() => void startDraft()} />}
          {tab === "accounts" && <AccountsPanel users={overview.users} busy={busy} notify={notify} reload={load} onRefresh={onRefresh} />}
          {tab === "draftLab" && <DraftLabAccessPanel notify={notify} />}
          {tab === "reset" && <ResetLeaguePanel busy={busy} onDownloadBackup={downloadLeagueBackup} onRestoreBackup={restoreLeagueBackup} onResetPoll={resetStandardDraftPoll} onResetProgress={resetProgress} onResetLeague={resetLeague} />}
        </div>
      </div>
    </div>
  );
}

const LEGACY_DRAFT_LAB_AUTH_KEY = "team-beer-draft-lab-commissioner-auth-v1";
const LEGACY_DRAFT_LAB_CONFIG_KEY = "team-beer-draft-lab-commissioner-config-v1";

interface DraftLabSetupStatus {
  initialized: boolean;
  passwordMinLength: number;
  passwordMaxLength: number;
  capabilityTtlSeconds: number;
}

interface DraftLabSetupCapability {
  capabilityToken: string;
  expiresAt: number;
}

interface DraftLabPasswordRecoveryResponse {
  passwordReset: true;
}

interface LegacyDraftLabSetup {
  config: DraftLabLeagueSetupConfig | null;
  auth: LegacyDraftLabAuth | null;
  configRaw: string | null;
  authRaw: string | null;
  found: boolean;
}

function readLegacyDraftLabSetup(): LegacyDraftLabSetup {
  try {
    const configRaw = window.localStorage.getItem(LEGACY_DRAFT_LAB_CONFIG_KEY);
    const authRaw = window.localStorage.getItem(LEGACY_DRAFT_LAB_AUTH_KEY);
    const auth = parseLegacyDraftLabAuth(authRaw);
    if (!configRaw) return { config: null, auth, configRaw, authRaw, found: authRaw !== null };
    const parsed: unknown = JSON.parse(configRaw);
    if (
      !isRecord(parsed)
      || parsed.version !== 1
      || !isRecord(parsed.aliases)
      || !isRecord(parsed.eligibleAdds)
      || !isRecord(parsed.eligibleRemoves)
    ) {
      return { config: null, auth, configRaw, authRaw, found: true };
    }
    const config: DraftLabLeagueSetupConfig = {
      version: 1,
      aliases: parsed.aliases as DraftLabLeagueSetupConfig["aliases"],
      eligibleAdds: parsed.eligibleAdds as DraftLabLeagueSetupConfig["eligibleAdds"],
      eligibleRemoves: parsed.eligibleRemoves as DraftLabLeagueSetupConfig["eligibleRemoves"],
    };
    const ordered: DraftLabLeagueSetupConfig = emptyDraftLabLeagueSetupConfig();
    for (const teamName of DRAFT_LAB_CANONICAL_TEAM_NAMES) {
      if (config.aliases[teamName]) ordered.aliases[teamName] = config.aliases[teamName];
      if (config.eligibleAdds[teamName]?.length) ordered.eligibleAdds[teamName] = [...config.eligibleAdds[teamName]];
      if (config.eligibleRemoves[teamName]?.length) ordered.eligibleRemoves[teamName] = [...config.eligibleRemoves[teamName]];
    }
    const isDefault = Object.keys(ordered.aliases).length === 0
      && Object.keys(ordered.eligibleAdds).length === 0
      && Object.keys(ordered.eligibleRemoves).length === 0;
    const canonical = JSON.stringify(ordered);
    let hash = 0x811c9dc5;
    for (let index = 0; index < canonical.length; index += 1) {
      hash ^= canonical.charCodeAt(index);
      hash = Math.imul(hash, 0x01000193);
    }
    const fingerprint = isDefault
      ? "default-v1"
      : `cfg-v1-${(hash >>> 0).toString(16).padStart(8, "0")}`;
    if (parsed.fingerprint !== fingerprint) {
      return { config: null, auth, configRaw, authRaw, found: true };
    }
    return {
      config: ordered,
      auth,
      configRaw,
      authRaw,
      found: true,
    };
  } catch {
    return { config: null, auth: null, configRaw: null, authRaw: null, found: false };
  }
}

function removeMatchingLegacyDraftLabSetup(legacy: LegacyDraftLabSetup): void {
  try {
    if (window.localStorage.getItem(LEGACY_DRAFT_LAB_CONFIG_KEY) === legacy.configRaw) {
      window.localStorage.removeItem(LEGACY_DRAFT_LAB_CONFIG_KEY);
    }
    if (window.localStorage.getItem(LEGACY_DRAFT_LAB_AUTH_KEY) === legacy.authRaw) {
      window.localStorage.removeItem(LEGACY_DRAFT_LAB_AUTH_KEY);
    }
  } catch {
    // The shared server setup is already authoritative. Storage cleanup is
    // best-effort and must never turn a successful initialization into an error.
  }
}

function draftLabPasswordPolicyError(password: string, confirmation: string): string {
  const length = Array.from(password).length;
  if (length < DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH || length > DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH) {
    return `Use ${DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH}–${DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} characters.`;
  }
  if (password !== confirmation) return "The new passwords do not match.";
  return "";
}

function draftLabAccessError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.code === "league_setup_invalid_password") return "The current shared Draft Lab password is incorrect.";
    if (error.code === "invalid_credentials" || error.code === "league_setup_reauthentication_failed") return "The League Hub account password is incorrect.";
    if (error.code === "league_setup_reauthentication_locked") {
      const wait = error.retryAfterSeconds ? ` Try again in ${error.retryAfterSeconds} seconds.` : " Try again later.";
      return `Too many incorrect League Hub password attempts.${wait}`;
    }
    if (error.code === "league_setup_unlock_locked") {
      const wait = error.retryAfterSeconds ? ` Try again in ${error.retryAfterSeconds} seconds.` : " Try again later.";
      return `Too many incorrect password attempts.${wait}`;
    }
    if (error.code === "league_setup_revision_conflict") return "Draft Lab setup changed in another browser. Reload this section and try again.";
    if (error.code === "league_setup_already_initialized") return "A shared Draft Lab password was created in another browser. Reload this section.";
    if (error.status === 409) return "Draft Lab access changed in another browser. Reload this section before trying again.";
    if (error.code === "authentication_required" || error.status === 401) return "Your League Hub session expired. Sign in again before resetting Draft Lab access.";
    if (error.code === "csrf_rejected" || error.code === "origin_rejected" || error.status === 403) return "League Hub could not verify this browser session. Reload the page, sign in again if asked, and retry.";
  }
  return error instanceof Error ? error.message : fallback;
}

function DraftLabAccessPanel({ notify }: { notify: (message: string, kind?: ToastMessage["kind"]) => void }) {
  const [status, setStatus] = useState<DraftLabSetupStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [legacyPassword, setLegacyPassword] = useState("");
  const [recoveryAccountPassword, setRecoveryAccountPassword] = useState("");
  const [recoveringPassword, setRecoveringPassword] = useState(false);
  const [recoveryConfirmed, setRecoveryConfirmed] = useState(false);
  const [legacy] = useState(readLegacyDraftLabSetup);
  const [migrateLegacy, setMigrateLegacy] = useState(Boolean(legacy.config && legacy.auth));
  const recoveryTriggerRef = useRef<HTMLButtonElement>(null);
  const recoveryAccountPasswordRef = useRef<HTMLInputElement>(null);
  const recoveryPanelId = useId();
  const recoveryHeadingId = useId();
  const recoveryWarningId = useId();

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const next = await api<DraftLabSetupStatus>(`${DRAFT_LAB_LEAGUE_SETUP_API_PREFIX}/status`);
      if (
        typeof next.initialized !== "boolean"
        || next.passwordMinLength !== DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH
        || next.passwordMaxLength !== DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH
        || next.capabilityTtlSeconds !== DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_TTL_SECONDS
      ) {
        throw new Error("Draft Lab access returned an invalid status.");
      }
      setStatus(next);
    } catch (caught) {
      setStatus(null);
      setError(draftLabAccessError(caught, "Unable to check Draft Lab access."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadStatus(); }, [loadStatus]);

  async function initialize(event: FormEvent) {
    event.preventDefault();
    const policyError = draftLabPasswordPolicyError(newPassword, confirmation);
    if (busy || policyError) {
      if (policyError) setError(policyError);
      return;
    }
    setBusy(true);
    setError("");
    try {
      if (migrateLegacy) {
        if (!legacy.config || !legacy.auth) {
          setError("The browser-only Draft Lab setup cannot be safely migrated.");
          return;
        }
        if (!await verifyLegacyDraftLabPassword(legacyPassword, legacy.auth)) {
          setError("The old browser-only League Setup password is incorrect.");
          return;
        }
      }
      await api(`${DRAFT_LAB_LEAGUE_SETUP_API_PREFIX}/initialize`, {
        method: "POST",
        body: JSON.stringify({
          password: newPassword,
          confirmation,
          config: migrateLegacy && legacy.config ? legacy.config : emptyDraftLabLeagueSetupConfig(),
        }),
      });
      removeMatchingLegacyDraftLabSetup(legacy);
      setNewPassword("");
      setConfirmation("");
      setLegacyPassword("");
      setStatus((current) => current ? { ...current, initialized: true } : current);
      notify("The shared Draft Lab password is now active on every browser and device.");
    } catch (caught) {
      setError(draftLabAccessError(caught, "Unable to create the shared Draft Lab password."));
      if (caught instanceof ApiError && caught.code === "league_setup_already_initialized") void loadStatus();
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    const policyError = draftLabPasswordPolicyError(newPassword, confirmation);
    if (busy || !currentPassword || policyError) {
      if (!currentPassword) setError("Enter the current shared Draft Lab password.");
      else if (policyError) setError(policyError);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const capability = await api<DraftLabSetupCapability>(`${DRAFT_LAB_LEAGUE_SETUP_API_PREFIX}/unlock`, {
        method: "POST",
        body: JSON.stringify({ password: currentPassword }),
      });
      await api<DraftLabSetupCapability>(`${DRAFT_LAB_LEAGUE_SETUP_API_PREFIX}/change-password`, {
        method: "POST",
        headers: { [DRAFT_LAB_LEAGUE_SETUP_CAPABILITY_HEADER]: capability.capabilityToken },
        body: JSON.stringify({ password: newPassword, confirmation }),
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmation("");
      notify("The shared Draft Lab password was changed. Existing Draft Lab unlocks were revoked.");
    } catch (caught) {
      setError(draftLabAccessError(caught, "Unable to change the shared Draft Lab password."));
    } finally {
      setBusy(false);
    }
  }

  function beginPasswordRecovery() {
    setError("");
    setCurrentPassword("");
    clearPasswordRecoverySecrets();
    setRecoveringPassword(true);
    requestAnimationFrame(() => recoveryAccountPasswordRef.current?.focus());
  }

  function clearPasswordRecoverySecrets() {
    setRecoveryAccountPassword("");
    setNewPassword("");
    setConfirmation("");
    setRecoveryConfirmed(false);
  }

  function cancelPasswordRecovery() {
    setError("");
    clearPasswordRecoverySecrets();
    setRecoveringPassword(false);
    requestAnimationFrame(() => recoveryTriggerRef.current?.focus());
  }

  async function recoverPassword(event: FormEvent) {
    event.preventDefault();
    const policyError = draftLabPasswordPolicyError(newPassword, confirmation);
    if (busy || !recoveryAccountPassword || !recoveryConfirmed || policyError) {
      if (!recoveryAccountPassword) setError("Enter your current League Hub account password.");
      else if (!recoveryConfirmed) setError("Confirm that every active Draft Lab unlock will be invalidated.");
      else if (policyError) setError(policyError);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const result = await api<DraftLabPasswordRecoveryResponse>(
        `${DRAFT_LAB_LEAGUE_SETUP_API_PREFIX}/recover-password`,
        {
          method: "POST",
          body: JSON.stringify({
            accountPassword: recoveryAccountPassword,
            password: newPassword,
            confirmation,
          }),
        },
      );
      if (result.passwordReset !== true) throw new Error("Draft Lab access returned an invalid recovery response.");
      clearPasswordRecoverySecrets();
      setRecoveringPassword(false);
      notify("The shared Draft Lab password was reset. Every active Draft Lab unlock was invalidated.");
      requestAnimationFrame(() => recoveryTriggerRef.current?.focus());
    } catch (caught) {
      const locked = caught instanceof ApiError && caught.status === 429;
      clearPasswordRecoverySecrets();
      if (caught instanceof ApiError && caught.status === 409) {
        void loadStatus();
      }
      setError(draftLabAccessError(caught, "Unable to reset the shared Draft Lab password."));
      if (locked) {
        setRecoveringPassword(false);
        requestAnimationFrame(() => recoveryTriggerRef.current?.focus());
      } else {
        requestAnimationFrame(() => recoveryAccountPasswordRef.current?.focus());
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="surface-card">
      <div className="card-header"><h2>Draft Lab access</h2>{status?.initialized ? <span className="status-pill status-pill--success">Shared password active</span> : null}</div>
      <div className="card-body stack">
        <p>The League Setup password is shared across browsers, devices, and private tabs. It is separate from your League Hub sign-in and can be created or changed only here by the commissioner.</p>
        <a className="button button--secondary" href="/teambeer/draftlab/" target="_blank" rel="noreferrer">Open Draft Lab ↗</a>
        {loading ? <p className="muted" role="status">Checking shared Draft Lab access…</p> : null}
        {!loading && !status ? <button className="button button--secondary" type="button" onClick={() => void loadStatus()}>Try again</button> : null}
        {status && !status.initialized ? (
          <form className="stack" onSubmit={(event) => void initialize(event)}>
            <div className="notice notice--warning"><span aria-hidden="true">!</span><span>This is the one-time global setup. Draft Lab will never offer anyone a create-password screen.</span></div>
            {legacy.config && legacy.auth ? <><label className="checkbox-row"><input type="checkbox" checked={migrateLegacy} onChange={(event) => setMigrateLegacy(event.target.checked)} /><span>Move the team names and eligible-player changes saved in this browser into the shared setup.</span></label>{migrateLegacy ? <label className="field"><span>Old browser-only League Setup password</span><input type="password" autoComplete="current-password" required value={legacyPassword} onChange={(event) => setLegacyPassword(event.target.value)} /><small className="field-hint">This verifies that the saved browser-only setup belongs to you. It is checked locally and is never sent to the server.</small></label> : null}</> : null}
            {legacy.found && (!legacy.config || !legacy.auth) ? <div className="alert alert--warning">Old browser-only Draft Lab data was found, but it could not be safely verified for migration. The shared setup will begin with the built-in team names and eligibles.</div> : null}
            <label className="field"><span>New shared Draft Lab password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /><small className="field-hint">Use {DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH}–{DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} characters. Everyone must enter this exact password to open League Setup.</small></label>
            <label className="field"><span>Confirm shared password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
            <button className="button button--primary" type="submit" disabled={busy}>{busy && <ButtonSpinner />}Create shared password</button>
          </form>
        ) : null}
        {status?.initialized ? (
          <>
            {!recoveringPassword ? <form className="stack" onSubmit={(event) => void changePassword(event)}>
              <h3>Change shared password</h3>
              <p className="muted">Changing it signs out every currently unlocked Draft Lab League Setup session. Draft simulations are not erased.</p>
              <label className="field"><span>Current shared Draft Lab password</span><input type="password" autoComplete="current-password" required value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} /></label>
              <label className="field"><span>New shared Draft Lab password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
              <label className="field"><span>Confirm new shared password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
              <button className="button button--primary" type="submit" disabled={busy}>{busy && <ButtonSpinner />}Change shared password</button>
            </form> : null}
            <section className="stack" aria-labelledby={recoveryHeadingId}>
              <h3 id={recoveryHeadingId}>Forgot the shared password?</h3>
              <p className="muted">The commissioner can replace it without the old shared Draft Lab password. For security, recovery verifies the current League Hub commissioner account password. Team names, eligible-player changes, and draft simulations are preserved.</p>
              <div className="button-row">
                <button ref={recoveryTriggerRef} className="button button--danger" type="button" disabled={busy || recoveringPassword} aria-expanded={recoveringPassword} aria-controls={recoveryPanelId} onClick={beginPasswordRecovery}>Reset shared password</button>
              </div>
              {recoveringPassword ? <form id={recoveryPanelId} className="stack" aria-labelledby={recoveryHeadingId} aria-describedby={recoveryWarningId} onSubmit={(event) => void recoverPassword(event)}>
                <div id={recoveryWarningId} className="alert alert--warning" role="alert"><span aria-hidden="true">!</span><span>This recovery immediately invalidates every active Draft Lab League Setup unlock on every browser, device, and private tab. The old shared password will stop working.</span></div>
                <label className="field"><span>Current League Hub account password</span><input ref={recoveryAccountPasswordRef} type="password" autoComplete="current-password" required value={recoveryAccountPassword} onChange={(event) => setRecoveryAccountPassword(event.target.value)} /><small className="field-hint">Use the password for your League Hub commissioner sign-in—not the forgotten shared Draft Lab password.</small></label>
                <label className="field"><span>New shared Draft Lab password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={newPassword} onChange={(event) => setNewPassword(event.target.value)} /></label>
                <label className="field"><span>Confirm new shared password</span><input type="password" autoComplete="new-password" minLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MIN_LENGTH} maxLength={DRAFT_LAB_LEAGUE_SETUP_PASSWORD_MAX_LENGTH} required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
                <label className="checkbox-row"><input type="checkbox" required checked={recoveryConfirmed} onChange={(event) => setRecoveryConfirmed(event.target.checked)} /><span>I understand that resetting the shared password will invalidate every active Draft Lab unlock.</span></label>
                <div className="button-row"><button className="button button--danger" type="submit" disabled={busy || !recoveryConfirmed}>{busy && <ButtonSpinner />}Reset and invalidate all unlocks</button><button className="button button--secondary" type="button" disabled={busy} onClick={cancelPasswordRecovery}>Cancel</button></div>
              </form> : null}
            </section>
          </>
        ) : null}
        {error ? <div className="alert alert--danger" role="alert">{error}</div> : null}
      </div>
    </section>
  );
}

function TeamNamesPanel({ teams, phase, busy, onRename, onUnsubmit }: {
  teams: AdminTeam[];
  phase: string;
  busy: boolean;
  onRename: (teamId: string, name: string) => Promise<void>;
  onUnsubmit: (teamId: string) => Promise<void>;
}) {
  return (
    <section className="surface-card">
      <div className="card-header"><h2>Team names</h2><span className="status-pill status-pill--info">Teams: {teams.length}</span></div>
      <div className="card-body stack">
        <p className="muted">Change the name shown throughout the League Hub and in future Draft Lab handoffs. Manager assignments stay attached to the same team. A submitted keeper form can be reopened here without deleting its choices.</p>
        <div className="team-name-grid">
          {teams.map((team) => <TeamNameEditor key={team.id} team={team} phase={phase} busy={busy} onRename={onRename} onUnsubmit={onUnsubmit} />)}
        </div>
      </div>
    </section>
  );
}

function TeamNameEditor({ team, phase, busy, onRename, onUnsubmit }: {
  team: AdminTeam;
  phase: string;
  busy: boolean;
  onRename: (teamId: string, name: string) => Promise<void>;
  onUnsubmit: (teamId: string) => Promise<void>;
}) {
  const fieldId = useId();
  const [name, setName] = useState(team.name);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmingUnsubmit, setConfirmingUnsubmit] = useState(false);
  const [unsubmitting, setUnsubmitting] = useState(false);
  const [unsubmitError, setUnsubmitError] = useState("");
  useEffect(() => { setName(team.name); setError(""); }, [team.name]);
  const normalizedName = name.trim().replace(/\s+/gu, " ");
  const changed = normalizedName !== team.name;
  const editablePhase = ["registration", "keeper_selection"].includes(phase);
  const canUnsubmit = team.submissionStatus === "submitted" && editablePhase;
  const unsubmitTitle = canUnsubmit
    ? "Reopen this team's submitted keeper choices for editing."
    : !editablePhase || team.submissionStatus === "locked"
      ? "Keeper selections cannot be reopened after Keeper Draft preparation begins."
      : team.submissionStatus === "draft"
        ? "This team's keeper selections have not been submitted."
        : "Only submitted keeper selections can be reopened.";

  async function save(event: FormEvent) {
    event.preventDefault();
    if (busy || !changed || !normalizedName) return;
    setError("");
    setSaving(true);
    try {
      await onRename(team.id, normalizedName);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to change the team name.");
    } finally {
      setSaving(false);
    }
  }

  async function unsubmit() {
    if (busy || unsubmitting || !canUnsubmit) return;
    setUnsubmitting(true);
    setUnsubmitError("");
    try {
      await onUnsubmit(team.id);
      setConfirmingUnsubmit(false);
    } catch (caught) {
      setUnsubmitError(caught instanceof Error ? caught.message : "Unable to reopen keeper selections.");
    } finally {
      setUnsubmitting(false);
    }
  }

  return (
    <form className="team-name-card" onSubmit={(event) => void save(event)}>
      <div className="team-name-card-heading"><strong>{team.name}</strong><span>{team.managerName ?? "No manager assigned"}</span></div>
      <label className="field" htmlFor={fieldId}><span>Team name</span><input id={fieldId} required minLength={1} maxLength={120} autoComplete="off" value={name} aria-invalid={Boolean(error)} onChange={(event) => { setName(event.target.value); setError(""); }} /></label>
      {error && <div className="alert alert--danger" role="alert">{error}</div>}
      <div className="button-row team-name-actions"><button className="button button--secondary button--small" type="submit" disabled={busy || !changed || !normalizedName}>{saving && <ButtonSpinner />}Save team name</button><button className="button button--danger button--small" type="button" disabled={busy || !canUnsubmit} title={unsubmitTitle} onClick={() => { setUnsubmitError(""); setConfirmingUnsubmit(true); }}>Unsubmit keepers</button></div>
      <ConfirmDialog open={confirmingUnsubmit} title={`Unsubmit keepers for ${team.name}?`} confirmLabel="Unsubmit keepers" danger busy={busy || unsubmitting} confirmDisabled={!canUnsubmit} onCancel={() => { setConfirmingUnsubmit(false); setUnsubmitError(""); }} onConfirm={() => void unsubmit()}>
        <p>This reopens only this team&apos;s submitted keeper form. Existing keeper choices are preserved, and the manager can edit and resubmit them.</p>
        <div className="alert alert--warning"><span aria-hidden="true">!</span><span>No Keeper Draft progress or other team&apos;s keeper submission will be reset.</span></div>
        {unsubmitError && <div className="alert alert--danger" role="alert">{unsubmitError}</div>}
      </ConfirmDialog>
    </form>
  );
}

type BackupDialogAction = { kind: "download"; variant: LeagueBackupVariant } | { kind: "restore" };

function LeagueBackupPanel({ busy, onDownloadBackup, onRestoreBackup }: {
  busy: boolean;
  onDownloadBackup: (variant: LeagueBackupVariant, password: string) => Promise<void>;
  onRestoreBackup: (preview: LeagueBackupPreview, password: string, idempotencyKey: string) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const fullDownloadRef = useRef<HTMLButtonElement>(null);
  const registrationsDownloadRef = useRef<HTMLButtonElement>(null);
  const restoreTriggerRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const inspectionId = useRef(0);
  const operationKey = useRef("");
  const [preview, setPreview] = useState<LeagueBackupPreview | null>(null);
  const [fileError, setFileError] = useState("");
  const [inspecting, setInspecting] = useState(false);
  const [action, setAction] = useState<BackupDialogAction | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [actionError, setActionError] = useState("");
  const restoreConfirmationMatches = action?.kind !== "restore" || confirmation === "RESTORE LEAGUE BACKUP";

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (action && !dialog.open) {
      dialog.showModal();
      window.requestAnimationFrame(() => passwordRef.current?.focus());
    } else if (!action && dialog.open) {
      dialog.close();
    }
  }, [action]);

  function beginDownload(variant: LeagueBackupVariant) {
    setPassword("");
    setConfirmation("");
    setActionError("");
    setAction({ kind: "download", variant });
  }

  function beginRestore() {
    if (!preview) return;
    if (!operationKey.current) operationKey.current = createClientId();
    setPassword("");
    setConfirmation("");
    setActionError("");
    setAction({ kind: "restore" });
  }

  function close(force = false) {
    if (busy && !force) return;
    const trigger = action?.kind === "restore"
      ? restoreTriggerRef.current
      : action?.kind === "download" && action.variant === "registrations"
        ? registrationsDownloadRef.current
        : fullDownloadRef.current;
    setAction(null);
    setPassword("");
    setConfirmation("");
    setActionError("");
    window.requestAnimationFrame(() => trigger?.focus());
  }

  async function inspectFile(file: File | null) {
    const currentInspection = ++inspectionId.current;
    setPreview(null);
    operationKey.current = "";
    setFileError("");
    if (!file) return;
    setInspecting(true);
    try {
      const nextPreview = await inspectLeagueBackupFile(file);
      if (inspectionId.current === currentInspection) {
        operationKey.current = createClientId();
        setPreview(nextPreview);
      }
    } catch (caught) {
      if (inspectionId.current === currentInspection) {
        setFileError(caught instanceof Error ? caught.message : "Unable to inspect this League Hub backup.");
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } finally {
      if (inspectionId.current === currentInspection) setInspecting(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !action || !password || !restoreConfirmationMatches) return;
    setActionError("");
    try {
      if (action.kind === "download") {
        await onDownloadBackup(action.variant, password);
      } else if (preview) {
        await onRestoreBackup(preview, password, operationKey.current);
        setPreview(null);
        operationKey.current = "";
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
      close(true);
    } catch (caught) {
      const code = caught instanceof ApiError ? caught.code : "";
      const incorrect = code === "password_confirmation_failed";
      const unsafeSignature = code === "backup_signature_invalid";
      const message = incorrect
        ? "Current password is incorrect."
        : caught instanceof Error
          ? caught.message
          : action.kind === "restore"
            ? "Unable to restore this League Hub backup."
            : "Unable to download this League Hub backup.";
      setActionError(action.kind === "restore" ? `${message} ${leagueRestoreFailureGuidance(code)}` : message);
      if (incorrect) {
        setPassword("");
        window.requestAnimationFrame(() => passwordRef.current?.focus());
      }
      if (unsafeSignature) {
        setPreview(null);
        operationKey.current = "";
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    }
  }

  const downloadVariant = action?.kind === "download" ? action.variant : null;
  return (
    <section className="surface-card backup-zone">
      <div className="card-header"><h2>League backups</h2><span className="status-pill status-pill--info">Commissioner Only</span></div>
      <div className="card-body stack">
        <div className="alert alert--warning" role="alert"><span aria-hidden="true">!</span><span>Backup files contain sensitive registration and preference data, including email addresses, account password hashes, verification state, team assignments, and poll rankings. Store them securely and do not email or share them.</span></div>
        <div className="backup-download-grid">
          <div className="backup-action-card">
            <div><strong>Full league backup</strong><p>Includes registrations, assignments, Standard Draft time poll rankings and finalized result, Standard Draft order, settings, keeper selections, and Keeper Draft progress.</p></div>
            <button ref={fullDownloadRef} className="button button--secondary" type="button" disabled={busy} onClick={() => beginDownload("full")}>Download full league backup</button>
          </div>
          <div className="backup-action-card">
            <div><strong>Registrations only</strong><p>Includes registered accounts, verification, memberships, team assignments, and Standard Draft time poll rankings and finalized result without keeper or draft progress.</p></div>
            <button ref={registrationsDownloadRef} className="button button--secondary" type="button" disabled={busy} onClick={() => beginDownload("registrations")}>Download registrations only</button>
          </div>
        </div>
        <div className="backup-restore-area">
          <div><h3>Restore a League Hub backup</h3><p className="muted">Choose a signed JSON backup created by this League Hub. The server verifies its signature and every reference before changing league data.</p></div>
          <label className="field"><span>League backup JSON file</span><input ref={fileInputRef} type="file" accept=".json,application/json" disabled={busy || inspecting} aria-describedby="backup-file-help" onChange={(event) => void inspectFile(event.currentTarget.files?.[0] ?? null)} /><small id="backup-file-help" className="field-hint">Selecting a file only reads its signed metadata. It does not change the league.</small></label>
          {inspecting && <div className="inline-progress" role="status"><ButtonSpinner />Inspecting signed backup metadata…</div>}
          {fileError && <div className="alert alert--danger" role="alert">{fileError} No league data was changed.</div>}
          {preview && (
            <div className="backup-file-preview" role="status">
              <div className="backup-file-preview__heading"><div><span>Ready for server verification</span><strong>{preview.fileName || "Selected League Hub backup"}</strong></div><span className={`status-pill ${preview.variant === "full" ? "status-pill--pending" : "status-pill--info"}`}>{preview.variant === "full" ? "Full League" : "Registrations Only"}</span></div>
              {preview.warnings.map((warning) => <div className="alert alert--warning backup-legacy-warning" role="alert" key={warning}><span aria-hidden="true">!</span><span>{warning}</span></div>)}
              <dl>
                <div><dt>Season</dt><dd>{preview.season}</dd></div>
                <div><dt>Exported</dt><dd>{new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(preview.exportedAtMs))}</dd></div>
                <div><dt>Accounts</dt><dd>{preview.summary.userCount}</dd></div>
                <div><dt>Assignments</dt><dd>{preview.summary.assignedTeamCount}/{preview.summary.teamCount}</dd></div>
                {backupPollBallotCount(preview) !== null && <div><dt>Poll rankings</dt><dd>{backupPollBallotCount(preview)}</dd></div>}
                {!preview.legacyNoFinalizationSnapshot && <div><dt>Poll result</dt><dd>{preview.summary.pollFinalized ? "Finalized" : "Not finalized"}</dd></div>}
                <div><dt>File size</dt><dd>{formatBackupFileSize(preview.sizeBytes)}</dd></div>
              </dl>
              <p className="field-hint">The signed payload stays private. Its signature and contents are authoritative only after the server validates them.</p>
              <button ref={restoreTriggerRef} className="button button--danger" type="button" disabled={busy} onClick={beginRestore}>Review and restore backup</button>
            </div>
          )}
        </div>
      </div>
      <dialog ref={dialogRef} className="modal" aria-labelledby="backup-action-title" onCancel={(event) => { event.preventDefault(); close(); }}>
        <form className="modal-card" onSubmit={(event) => void submit(event)}>
          <div className="modal-header"><h2 id="backup-action-title">{action?.kind === "restore" ? "Restore League Hub backup?" : downloadVariant === "full" ? "Download full league backup?" : "Download registrations-only backup?"}</h2><CloseButton onClick={() => close()} disabled={busy} /></div>
          <div className="modal-body stack">
            {action?.kind === "restore" && preview ? <>
              <div className="alert alert--danger"><span aria-hidden="true">!</span><span>Restoring this {preview.variant === "full" ? "full league" : "registrations-only"} backup signs out every manager and commissioner, including you. Everyone must sign in again.</span></div>
              {preview.warnings.map((warning) => <div className="alert alert--warning backup-legacy-warning" role="alert" key={warning}><span aria-hidden="true">!</span><span>{warning}</span></div>)}
              {preview.legacyNoPollSnapshot ? <p><strong>Legacy restore:</strong> this file has no poll snapshot. All current Standard Draft time poll rankings will be cleared, and no poll rankings will be restored. {preview.variant === "full" ? "Registrations, assignments, Standard Draft order, settings, keeper selections, and Keeper Draft progress are replaced." : <><span>Registrations, verification, memberships, and assignments are replaced.</span> <strong>Current Standard Draft order, keeper selections, and Keeper Draft progress remain untouched.</strong></>}</p> : preview.variant === "full" ? <p><strong>Full restore:</strong> registrations, team assignments, Standard Draft time poll rankings, Standard Draft order, settings, keeper selections, and Keeper Draft progress are replaced by the backup.</p> : <p><strong>Registrations-only restore:</strong> registered accounts, verification, memberships, team assignments, and Standard Draft time poll rankings are replaced. <strong>Current Standard Draft order, keeper selections, and Keeper Draft progress remain untouched.</strong></p>}
              <p className="muted">The server will reject an invalid signature or unsafe reference before making any change. The file is never repaired or normalized in your browser.</p>
            </> : <>
              <div className="alert alert--warning"><span aria-hidden="true">!</span><span>The downloaded JSON contains sensitive account and registration data, including password hashes. Store it securely.</span></div>
              <p>{downloadVariant === "full" ? "The full backup includes registrations, assignments, Standard Draft time poll rankings, Standard Draft order, settings, keeper selections, and Keeper Draft progress." : "The registrations-only backup includes Standard Draft time poll rankings and preserves the current Standard Draft order, keeper selections, and Keeper Draft progress when restored."}</p>
            </>}
            <label className="field"><span>Current commissioner password</span><input ref={passwordRef} type="password" name="backupPassword" autoComplete="current-password" required value={password} aria-invalid={Boolean(actionError)} onChange={(event) => setPassword(event.target.value)} /></label>
            {action?.kind === "restore" && <label className="field"><span>Type RESTORE LEAGUE BACKUP to confirm</span><input name="backupConfirmation" autoComplete="off" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>}
            {actionError && <div className="alert alert--danger" role="alert">{actionError}</div>}
          </div>
          <div className="modal-actions">
            <button className="button button--quiet" type="button" disabled={busy} onClick={() => close()}>Cancel</button>
            <button className={action?.kind === "restore" ? "button button--danger" : "button button--primary"} type="submit" disabled={busy || !password || !restoreConfirmationMatches}>{busy && <ButtonSpinner />}{action?.kind === "restore" ? "Restore and sign everyone out" : "Download backup"}</button>
          </div>
        </form>
      </dialog>
    </section>
  );
}

function ResetLeaguePanel({ busy, onDownloadBackup, onRestoreBackup, onResetPoll, onResetProgress, onResetLeague }: {
  busy: boolean;
  onDownloadBackup: (variant: LeagueBackupVariant, password: string) => Promise<void>;
  onRestoreBackup: (preview: LeagueBackupPreview, password: string, idempotencyKey: string) => Promise<void>;
  onResetPoll: (accountPassword: string, idempotencyKey: string) => Promise<void>;
  onResetProgress: (password: string, idempotencyKey: string) => Promise<void>;
  onResetLeague: (password: string, idempotencyKey: string) => Promise<void>;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const pollTriggerRef = useRef<HTMLButtonElement>(null);
  const progressTriggerRef = useRef<HTMLButtonElement>(null);
  const leagueTriggerRef = useRef<HTMLButtonElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const operationKey = useRef("");
  const [action, setAction] = useState<"poll" | "progress" | "league" | null>(null);
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const confirmationPhrase = action === "poll" ? "RESET POLL" : action === "progress" ? "RESET PROGRESS" : "RESET LEAGUE";
  const confirmationMatches = confirmation === confirmationPhrase;

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (action && !dialog.open) {
      dialog.showModal();
      window.requestAnimationFrame(() => passwordRef.current?.focus());
    } else if (!action && dialog.open) {
      dialog.close();
    }
  }, [action]);

  function begin(nextAction: "poll" | "progress" | "league") {
    operationKey.current = createClientId();
    setPassword("");
    setConfirmation("");
    setError("");
    setAction(nextAction);
  }

  function close(force = false) {
    if (busy && !force) return;
    const trigger = action === "poll" ? pollTriggerRef.current : action === "progress" ? progressTriggerRef.current : leagueTriggerRef.current;
    setAction(null);
    setPassword("");
    setConfirmation("");
    setError("");
    window.requestAnimationFrame(() => trigger?.focus());
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !action || !password || !confirmationMatches) return;
    setError("");
    try {
      if (action === "poll") await onResetPoll(password, operationKey.current);
      else if (action === "progress") await onResetProgress(password, operationKey.current);
      else await onResetLeague(password, operationKey.current);
      close(true);
    } catch (caught) {
      const incorrect = caught instanceof ApiError && ["password_confirmation_failed", "poll_reauthentication_failed"].includes(caught.code);
      const fallback = action === "poll" ? "Unable to reset the draft-time poll." : action === "progress" ? "Unable to reset keeper progress." : "Unable to reset league data.";
      setError(incorrect ? "The League Hub account password is incorrect." : caught instanceof Error ? caught.message : fallback);
      if (action === "poll" || incorrect) {
        setPassword("");
        if (action === "poll") setConfirmation("");
        window.requestAnimationFrame(() => passwordRef.current?.focus());
      }
    }
  }

  return (
    <>
      <LeagueBackupPanel busy={busy} onDownloadBackup={onDownloadBackup} onRestoreBackup={onRestoreBackup} />
      <section className="surface-card reset-poll-zone">
        <div className="card-header"><h2>Reset draft-time poll</h2><span className="status-pill status-pill--info">Keeps League Data</span></div>
        <div className="card-body stack">
          <p>Delete every saved Standard Draft time ranking and reopen the poll from an empty state.</p>
          <p className="muted">Registrations, verified accounts, team assignments, keeper selections, Keeper Draft progress, teams, and settings remain unchanged.</p>
          <div className="alert alert--warning" role="alert"><span aria-hidden="true">!</span><span>All poll rankings and point totals will be deleted.</span></div>
          <div className="button-row"><button ref={pollTriggerRef} className="button button--danger" type="button" onClick={() => begin("poll")} disabled={busy}>Reset poll responses</button></div>
        </div>
      </section>
      <section className="surface-card reset-progress-zone">
        <div className="card-header"><h2>Reset keeper progress</h2><span className="status-pill status-pill--info">Keeps Accounts</span></div>
        <div className="card-body stack">
          <p>Start keeper selections and the Keeper Draft over without asking managers to register again or reassigning their teams.</p>
          <p className="muted">Registrations, verified accounts, active sessions, team assignments, team names, eligible-player lists, league links, settings, and Standard Draft time poll rankings remain. Keeper selections, Keeper Draft state, chat, picks, results, and related draft notifications are cleared.</p>
          <div className="alert alert--warning" role="alert"><span aria-hidden="true">!</span><span>Keeper selections and draft progress cannot be recovered after this reset.</span></div>
          <div className="button-row"><button ref={progressTriggerRef} className="button button--danger" type="button" onClick={() => begin("progress")} disabled={busy}>Reset keeper progress</button></div>
        </div>
      </section>
      <section className="surface-card danger-zone">
        <div className="card-header"><h2>Reset registrations and progress</h2><span className="status-pill status-pill--danger">Destructive</span></div>
        <div className="card-body stack">
          <p>Return the current league cycle to its pre-registration state. This removes every manager registration except your commissioner account, clears all team assignments and keeper selections, and removes Standard Draft time poll rankings, Keeper Draft lobby, chat, picks, results, notifications, and schedule progress.</p>
          <p className="muted">Teams, eligible-player lists, player data, Sleeper links, and your commissioner account remain.</p>
          <div className="alert alert--danger" role="alert"><span aria-hidden="true">!</span><span>This cannot be undone.</span></div>
          <div className="button-row"><button ref={leagueTriggerRef} className="button button--danger" type="button" onClick={() => begin("league")} disabled={busy}>Reset league data</button></div>
        </div>
      </section>
      <dialog
        ref={dialogRef}
        className="modal"
        aria-labelledby="reset-action-title"
        aria-describedby="reset-action-scope"
        onCancel={(event) => { event.preventDefault(); close(); }}
      >
        <form className="modal-card" onSubmit={(event) => void submit(event)}>
          <div className="modal-header"><h2 id="reset-action-title">{action === "poll" ? "Reset draft-time poll?" : action === "progress" ? "Reset keeper progress?" : "Reset registrations and progress?"}</h2><CloseButton onClick={() => close()} disabled={busy} /></div>
          <div className="modal-body stack" id="reset-action-scope">
            <div className="alert alert--danger"><span aria-hidden="true">!</span><span>This cannot be undone.</span></div>
            {action === "poll" ? <>
              <p>Every saved Standard Draft time ranking and the current point totals will be deleted.</p>
              <p className="muted"><strong>Registrations, team assignments, keeper selections, and all Keeper Draft progress will remain.</strong></p>
            </> : action === "progress" ? <>
              <p>Every keeper choice, Keeper Draft record, chat message, presence record, pick, result, and Keeper Draft notification will be removed.</p>
              <p className="muted"><strong>All registrations and team assignments will remain.</strong> Standard Draft time poll rankings, team names, player data, eligible-player lists, league links, settings, and active accounts also remain.</p>
            </> : <>
              <p>All manager registrations except your commissioner account, every team assignment, Standard Draft time poll ranking, keeper choice, Keeper Draft record, chat message, presence record, pending notification, and scheduled league date will be removed.</p>
              <p className="muted">League teams, player data, eligible-player lists, Sleeper links, and your commissioner account will remain.</p>
            </>}
            <label className="field"><span>{action === "poll" ? "Current League Hub account password" : "Current commissioner password"}</span><input ref={passwordRef} type="password" name="currentPassword" autoComplete="current-password" required value={password} aria-invalid={Boolean(error)} onChange={(event) => setPassword(event.target.value)} /></label>
            <label className="field"><span>Type {confirmationPhrase} to confirm</span><input name="confirmation" autoComplete="off" required value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
            {error && <div className="alert alert--danger" role="alert">{error}</div>}
          </div>
          <div className="modal-actions">
            <button className="button button--quiet" type="button" onClick={() => close()} disabled={busy}>Cancel</button>
            <button className="button button--danger" type="submit" disabled={busy || !password || !confirmationMatches}>{busy && <ButtonSpinner />}{action === "poll" ? "Reset poll responses" : action === "progress" ? "Reset keeper progress" : "Reset league"}</button>
          </div>
        </form>
      </dialog>
    </>
  );
}

function TeamStatusTable({ teams }: { teams: AdminTeam[] }) {
  return (
    <div className="table-shell league-status-table-shell"><table className="data-table league-status-table"><thead><tr><th>Team</th><th>Manager</th><th>Keepers</th><th>Status</th></tr></thead><tbody>{teams.map((team) => <tr key={team.id}><td className="table-primary">{team.name}</td><td>{team.managerName ?? "Unassigned"}</td><td>{team.keeperCount}/10</td><td><span className={`status-pill ${["submitted", "locked"].includes(team.submissionStatus) ? "status-pill--success" : "status-pill--pending"}`}>{team.submissionStatus === "submitted" ? "Submitted" : team.submissionStatus === "locked" ? "Submitted" : submissionStatusLabel(team.submissionStatus)}</span></td></tr>)}</tbody></table></div>
  );
}

function AssignmentPanel({ users, teams, busy, onAssign }: {
  users: AdminUser[];
  teams: AdminTeam[];
  busy: boolean;
  onAssign: (userId: string, teamId: string) => Promise<void>;
}) {
  const eligibleUsers = users.filter((user) => user.verified && user.status === "active");
  return (
    <section className="surface-card"><div className="card-header"><h2>Assign managers to teams</h2><span className="status-pill status-pill--info">Verified Accounts Only</span></div><div className="card-body assignment-grid">{teams.map((team) => <AssignmentCard key={team.id} team={team} users={eligibleUsers} busy={busy} onAssign={onAssign} />)}</div></section>
  );
}

function AssignmentCard({ team, users, busy, onAssign }: {
  team: AdminTeam;
  users: AdminUser[];
  busy: boolean;
  onAssign: (userId: string, teamId: string) => Promise<void>;
}) {
  const [userId, setUserId] = useState(team.userId ?? "");
  useEffect(() => setUserId(team.userId ?? ""), [team.userId]);
  return (
    <div className="assignment-card"><div><strong>{team.name}</strong><p className="field-hint">{team.managerName ?? "No manager assigned"}</p></div><label className="field"><span>Manager</span><select value={userId} onChange={(event) => setUserId(event.target.value)}><option value="">Choose verified account</option>{users.map((user) => <option key={user.id} value={user.id}>{user.teamName ?? user.username} · {user.email}</option>)}</select>{userId && userId === team.userId && <small className="field-hint field-warning" role="status">Manager has already been assigned to this team.</small>}</label><button className="button button--secondary button--wide" type="button" disabled={busy || !userId || userId === team.userId} onClick={() => void onAssign(userId, team.id)}>Save assignment</button></div>
  );
}

interface AdminEligible {
  eligibleId: string;
  playerId: string;
  name: string;
  nflTeam?: string;
  positions?: string[];
  byeWeek?: number | null;
  isRookie?: boolean;
  isSelected?: boolean;
}

function normalizePositions(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((entry): entry is string => typeof entry === "string");
  if (typeof value !== "string" || value.length === 0) return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((entry): entry is string => typeof entry === "string") : [];
  } catch {
    return value.split(/[\/,]/u).map((entry) => entry.trim()).filter(Boolean);
  }
}

function normalizeAdminEligible(value: unknown): AdminEligible | null {
  if (!isRecord(value)) return null;
  const playerId = asString(value.playerId ?? value.player_id ?? value.id);
  const name = asString(value.name ?? value.full_name);
  if (!playerId || !name) return null;
  return {
    eligibleId: asString(value.eligibleId ?? value.eligible_id),
    playerId,
    name,
    nflTeam: asString(value.nflTeam ?? value.nfl_team, "FA"),
    positions: normalizePositions(value.positions ?? value.positions_json),
    byeWeek: value.byeWeek == null && value.bye_week == null ? null : asNumber(value.byeWeek ?? value.bye_week),
    isRookie: Boolean(value.isRookie ?? value.is_rookie),
    isSelected: Boolean(value.isSelected ?? value.is_selected),
  };
}

function EligibilityPanel({ teams, notify }: {
  teams: AdminTeam[];
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
}) {
  const [teamId, setTeamId] = useState(teams[0]?.id ?? "");
  const [eligibles, setEligibles] = useState<AdminEligible[]>([]);
  const [available, setAvailable] = useState<AdminEligible[]>([]);
  const [query, setQuery] = useState("");
  const [candidate, setCandidate] = useState("");
  const [loading, setLoading] = useState(false);
  const [supported, setSupported] = useState(true);

  const load = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    try {
      const raw = await api<unknown>(`/api/admin/eligibles?teamId=${encodeURIComponent(teamId)}&q=${encodeURIComponent(query)}`);
      const data = isRecord(raw) ? raw : {};
      setEligibles(Array.isArray(data.eligibles) ? data.eligibles.map(normalizeAdminEligible).filter((player): player is AdminEligible => player !== null) : []);
      setAvailable(Array.isArray(data.searchResults) ? data.searchResults.map(normalizeAdminEligible).filter((player): player is AdminEligible => player !== null && isKeeperEligibilityPlayer(player)) : []);
      setSupported(true);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) setSupported(false);
      else notify(error instanceof Error ? error.message : "Unable to load eligibles.", "error");
    } finally { setLoading(false); }
  }, [teamId, query, notify]);
  useEffect(() => {
    const timeout = window.setTimeout(() => { void load(); }, query ? 220 : 0);
    return () => window.clearTimeout(timeout);
  }, [load, query]);
  useEffect(() => {
    setCandidate("");
    setQuery("");
  }, [teamId]);

  async function add() {
    if (!candidate) return;
    setLoading(true);
    try {
      await api("/api/admin/eligibles", { method: "POST", body: JSON.stringify({ teamId, playerId: candidate }) });
      setCandidate(""); notify("Eligible player added."); await load();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to add eligible.", "error"); }
    finally { setLoading(false); }
  }
  async function remove(eligibleId: string) {
    setLoading(true);
    try {
      await api(`/api/admin/eligibles/${encodeURIComponent(eligibleId)}`, { method: "DELETE" });
      notify("Eligible player removed."); await load();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to remove eligible.", "error"); }
    finally { setLoading(false); }
  }

  return (
    <section className="surface-card">
      <div className="card-header"><h2>Eligible players</h2><span className="status-pill status-pill--info">League-Controlled</span></div>
      <div className="card-body stack">
        <label className="field"><span>Team</span><select value={teamId} onChange={(event) => setTeamId(event.target.value)}>{teams.map((team) => <option key={team.id} value={team.id}>{team.name}</option>)}</select></label>
        {!supported ? <div className="notice notice--warning"><span>!</span><span>The eligibility-management API has not been enabled in this local worker yet. The interface is ready and will activate when that route is present.</span></div> : <>
          <div className="form-grid">
            <label className="field"><span>Find a player to add</span><input type="search" value={query} onChange={(event) => { setQuery(event.target.value); setCandidate(""); }} placeholder="Type at least 2 letters" autoComplete="off" /><small className="field-hint">Offensive players, kickers, and individual defensive players are available. Team DEF/ST units are excluded.</small></label>
            <label className="field"><span>Search results</span><select value={candidate} disabled={query.trim().length < 2 || available.length === 0} onChange={(event) => setCandidate(event.target.value)}><option value="">{query.trim().length < 2 ? "Enter a player name" : available.length ? "Choose matching player" : "No available matches"}</option>{available.map((player) => <option key={player.playerId} value={player.playerId}>{player.name} · {player.positions?.join("/") || "Player"} · {player.nflTeam}</option>)}</select></label>
            <div className="span-2 button-row"><button className="button button--secondary" type="button" disabled={loading || !candidate} onClick={() => void add()}>{loading && <ButtonSpinner />}Add eligible</button></div>
          </div>
          <div className="table-shell"><table className="data-table"><thead><tr><th>Player</th><th>NFL team</th><th>Position</th><th>Bye</th><th>Action</th></tr></thead><tbody>{eligibles.map((player) => <tr key={player.eligibleId}><td className="table-primary">{player.name}{player.isRookie ? <span className="rookie-badge">Rookie</span> : null}</td><td>{player.nflTeam ?? "FA"}</td><td>{player.positions?.join("/") || "—"}</td><td>{player.byeWeek ?? "—"}</td><td><button className="button button--danger button--small" type="button" disabled={loading || player.isSelected} title={player.isSelected ? "A selected keeper cannot be removed" : undefined} onClick={() => void remove(player.eligibleId)}>{player.isSelected ? "Selected" : "Remove"}</button></td></tr>)}</tbody></table></div>
        </>}
      </div>
    </section>
  );
}

function SchedulePanel({ overview, busy, notify, reload, onInitialize, onStart }: {
  overview: AdminOverview;
  busy: boolean;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  reload: () => Promise<void>;
  onInitialize: () => void;
  onStart: () => void;
}) {
  const [keeperDeadline, setKeeperDeadline] = useState(localDateTimeValue(overview.settings.keeper_selection_deadline_at));
  const phase = overview.settings.phase;
  const lobbyReady = phase === "keeper_draft_lobby";
  const lobbyRecoverable = phase === "keeper_selection_locked";
  const draftActive = phase === "keeper_draft_active";
  const noKeeperDraftNeeded = phase === "standard_draft_pending";
  const draftComplete = ["keeper_draft_complete", "complete"].includes(phase);
  const draftStatus = noKeeperDraftNeeded ? "Not required" : draftComplete ? "Complete" : draftActive ? "In progress" : lobbyReady ? "Lobby ready" : lobbyRecoverable ? "Ready to initialize" : "Waiting for keepers";
  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await api("/api/admin/settings", { method: "PATCH", body: JSON.stringify({ keeperDeadlineAt: keeperDeadline ? Math.floor(new Date(keeperDeadline).getTime() / 1000) : null }) });
      notify("League schedule saved."); await reload();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to save schedule.", "error"); }
  }
  return (
    <>
      <section className="surface-card"><div className="card-header"><h2>Draft schedule</h2><span className="status-pill status-pill--info">Pacific Time</span></div><div className="card-body"><form className="form-grid schedule-form" onSubmit={(event) => void save(event)}><label className="field"><span>Keeper deadline</span><input type="datetime-local" value={keeperDeadline} onChange={(event) => setKeeperDeadline(event.target.value)} /></label><label className="field"><span>Standard Draft on Sleeper</span><input type="datetime-local" value={localDateTimeValue(overview.settings.standard_draft_at)} readOnly disabled aria-describedby="standard-draft-poll-schedule-note" /><small id="standard-draft-poll-schedule-note" className="muted">Set automatically by the final ranked-choice poll result.</small></label><div className="span-2 button-row"><button className="button button--primary" type="submit" disabled={busy}>Save schedule</button></div></form></div></section>
      <section className="surface-card">
        <div className="card-header"><h2>Live Keeper Draft</h2><span className={`status-pill ${draftComplete ? "status-pill--success" : "status-pill--info"}`}>{pillLabel(draftStatus)}</span></div>
        <div className="card-body stack">
          {noKeeperDraftNeeded ? (
            <p>Every team filled all 10 keeper spots, so no live Keeper Draft is required. The Standard Draft is next.</p>
          ) : draftComplete ? (
            <p>The Keeper Draft is complete and its results are locked.</p>
          ) : draftActive ? (
            <p>The Keeper Draft is currently in progress. Managers can return to the live draft room at any time.</p>
          ) : (
            <>
              <p>{lobbyReady ? "The persistent lobby is ready. Start only when managers are ready." : lobbyRecoverable ? "Every team has submitted. Initialize the persistent Keeper Draft lobby." : "These controls become available after every team submits its keepers."}</p>
              <div className="button-row">
                <button className="button button--secondary" type="button" disabled={busy || (!lobbyReady && !lobbyRecoverable)} onClick={onInitialize}>Initialize lobby</button>
                <button className="button button--primary" type="button" disabled={busy || !lobbyReady} onClick={onStart}>Start Keeper Draft</button>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

function AccountsPanel({ users, busy, notify, reload, onRefresh }: {
  users: AdminUser[];
  busy: boolean;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  reload: () => Promise<void>;
  onRefresh: () => Promise<void>;
}) {
  async function reset(userId: string) {
    try {
      await api("/api/admin/password-reset", { method: "POST", body: JSON.stringify({ userId }) });
      notify("Password-reset email queued. The existing password was never exposed.");
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to send password reset.", "error"); }
  }
  return (
    <section className="surface-card"><div className="card-header"><h2>Manager accounts</h2><span className="status-pill status-pill--info">Passwords Remain Private</span></div><div className="card-body accounts-grid">{users.map((user) => <AccountEditor key={user.id} user={user} busy={busy} notify={notify} reload={reload} onRefresh={onRefresh} onReset={reset} />)}</div></section>
  );
}

function AccountEditor({ user, busy, notify, reload, onRefresh, onReset }: {
  user: AdminUser;
  busy: boolean;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  reload: () => Promise<void>;
  onRefresh: () => Promise<void>;
  onReset: (userId: string) => Promise<void>;
}) {
  const usernameId = useId();
  const emailId = useId();
  const removeTitleId = useId();
  const removeBodyId = useId();
  const removePasswordId = useId();
  const removeConfirmationId = useId();
  const removeDialogRef = useRef<HTMLDialogElement>(null);
  const removeTriggerRef = useRef<HTMLButtonElement>(null);
  const removePasswordRef = useRef<HTMLInputElement>(null);
  const removeOperationKey = useRef("");
  const [username, setUsername] = useState(user.username);
  const [email, setEmail] = useState(user.email);
  const [disabled, setDisabled] = useState(user.status === "disabled");
  const [removalOpen, setRemovalOpen] = useState(false);
  const [removalPassword, setRemovalPassword] = useState("");
  const [removalConfirmation, setRemovalConfirmation] = useState("");
  const [removalError, setRemovalError] = useState("");
  const [removing, setRemoving] = useState(false);
  const expectedRemovalConfirmation = `REMOVE ${user.username}`;
  const commissionerProtected = user.role === "commissioner";

  useEffect(() => {
    setUsername(user.username);
    setEmail(user.email);
    setDisabled(user.status === "disabled");
  }, [user.email, user.status, user.username]);

  useEffect(() => {
    const dialog = removeDialogRef.current;
    if (!dialog) return;
    if (removalOpen && !dialog.open) {
      dialog.showModal();
      window.requestAnimationFrame(() => removePasswordRef.current?.focus());
    } else if (!removalOpen && dialog.open) {
      dialog.close();
    }
  }, [removalOpen]);

  function beginRemoval() {
    if (busy || removing || commissionerProtected) return;
    removeOperationKey.current = createClientId();
    setRemovalPassword("");
    setRemovalConfirmation("");
    setRemovalError("");
    setRemovalOpen(true);
  }

  function closeRemoval(force = false) {
    if ((busy || removing) && !force) return;
    setRemovalOpen(false);
    setRemovalPassword("");
    setRemovalConfirmation("");
    setRemovalError("");
    removeOperationKey.current = "";
    window.requestAnimationFrame(() => removeTriggerRef.current?.focus());
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    try {
      await api(`/api/admin/users/${encodeURIComponent(user.id)}`, { method: "PATCH", body: JSON.stringify({ username, email, disabled }) });
      notify("Account updated."); await reload();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to update account.", "error"); }
  }

  async function remove(event: FormEvent) {
    event.preventDefault();
    if (busy || removing || commissionerProtected || !removalPassword || removalConfirmation !== expectedRemovalConfirmation) return;
    setRemoving(true);
    setRemovalError("");
    try {
      await api(`/api/admin/users/${encodeURIComponent(user.id)}/remove`, {
        method: "POST",
        body: JSON.stringify({
          password: removalPassword,
          confirmation: removalConfirmation,
          idempotencyKey: removeOperationKey.current || createClientId(),
        }),
      });
    } catch (error) {
      setRemovalError(error instanceof Error ? error.message : "Unable to remove this account.");
      setRemoving(false);
      return;
    }

    closeRemoval(true);
    notify(`${user.username} was removed. Their original email and username can register again.`);
    try {
      await Promise.all([reload(), onRefresh()]);
    } catch (error) {
      notify(error instanceof Error ? `Account removed, but the page could not fully refresh: ${error.message}` : "Account removed, but the page could not fully refresh.", "error");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <article className="assignment-card account-card">
      <form className="stack" onSubmit={(event) => void save(event)}>
        <div className="account-card-heading"><strong>{user.teamName ?? user.username}</strong><span className={`status-pill ${user.verified ? "status-pill--success" : "status-pill--pending"}`}>{user.verified ? "Verified" : "Pending Email"}</span></div>
        <label className="field" htmlFor={usernameId}><span>Username</span><input id={usernameId} value={username} onChange={(event) => setUsername(event.target.value)} /></label>
        <label className="field" htmlFor={emailId}><span>Email</span><input id={emailId} type="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label className="checkbox-row"><input type="checkbox" checked={disabled} onChange={(event) => setDisabled(event.target.checked)} /><span>Disable account</span></label>
        <div className="button-row account-actions"><button className="button button--quiet button--small" type="button" disabled={busy || removing} onClick={() => void onReset(user.id)}>Send reset</button><button ref={removeTriggerRef} className="button button--danger button--small" type="button" disabled={busy || removing || commissionerProtected} title={commissionerProtected ? "Commissioner accounts cannot be removed." : "Remove this account and release its email and username for registration."} aria-haspopup="dialog" onClick={beginRemoval}>Remove</button><button className="button button--secondary button--small" type="submit" disabled={busy || removing}>Save</button></div>
      </form>
      <dialog ref={removeDialogRef} className="modal" aria-labelledby={removeTitleId} aria-describedby={removeBodyId} onCancel={(event) => { event.preventDefault(); closeRemoval(); }}>
        <form className="modal-card" aria-busy={removing} onSubmit={(event) => void remove(event)}>
          <div className="modal-header"><h2 id={removeTitleId}>Remove {user.username}?</h2><CloseButton onClick={() => closeRemoval()} disabled={busy || removing} /></div>
          <div className="modal-body stack" id={removeBodyId}>
            <div className="alert alert--warning"><span aria-hidden="true">!</span><span>This removes the account, league membership, team assignment, and Standard Draft time poll response.</span></div>
            <p>Existing keeper choices are preserved for {user.teamName ?? "the team"}. The original email and username may register again.</p>
            <label className="field" htmlFor={removePasswordId}><span>Current commissioner password</span><input ref={removePasswordRef} id={removePasswordId} type="password" autoComplete="current-password" required value={removalPassword} onChange={(event) => { setRemovalPassword(event.target.value); setRemovalError(""); }} /></label>
            <label className="field" htmlFor={removeConfirmationId}><span>Type <strong>{expectedRemovalConfirmation}</strong> to confirm</span><input id={removeConfirmationId} type="text" autoComplete="off" spellCheck={false} required value={removalConfirmation} onChange={(event) => { setRemovalConfirmation(event.target.value); setRemovalError(""); }} /></label>
            {removalError && <div className="alert alert--danger" role="alert">{removalError}</div>}
          </div>
          <div className="modal-actions"><button className="button button--quiet" type="button" disabled={busy || removing} onClick={() => closeRemoval()}>Cancel</button><button className="button button--danger" type="submit" disabled={busy || removing || !removalPassword || removalConfirmation !== expectedRemovalConfirmation}>{removing && <ButtonSpinner />}Remove account</button></div>
        </form>
      </dialog>
    </article>
  );
}

function KeeperDraft({ session, summary, notify, onReturn }: {
  session: SessionInfo;
  summary: LeagueSummary;
  notify: (message: string, kind?: ToastMessage["kind"]) => void;
  onReturn: () => void;
}) {
  const [snapshot, setSnapshot] = useState<DraftSnapshotView | null>(null);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState("ALL");
  const [chat, setChat] = useState("");
  const [preparation, setPreparation] = useState<PickPreparation | null>(null);
  const [confirmingWithdrawal, setConfirmingWithdrawal] = useState(false);
  const [withdrawChoiceRequired, setWithdrawChoiceRequired] = useState(false);
  const [withdrawOptions, setWithdrawOptions] = useState<WithdrawalKeeperOption[]>([]);
  const [withdrawOptionsLoading, setWithdrawOptionsLoading] = useState(false);
  const [withdrawOptionsError, setWithdrawOptionsError] = useState("");
  const [withdrawSearch, setWithdrawSearch] = useState("");
  const [specialKeeperPlayerId, setSpecialKeeperPlayerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [fitView, setFitView] = useState<KeeperDraftFitView | null>(null);
  const chatMessages = useRef<HTMLDivElement>(null);
  const draftLoadCoordinator = useRef(createLatestRequestCoordinator());
  const fitRequestSequence = useRef(0);
  const soundSession = useRef<KeeperDraftSoundSession | null>(null);
  const keeperEligibleAvailablePlayers = snapshot?.availablePlayers.filter(isKeeperEligibilityPlayer) ?? [];
  const availableFitSignature = keeperEligibleAvailablePlayers.map((player) => player.id).join("\u001f");
  const keeperLedgerSignature = snapshot?.keeperLedger
    .map((entry) => `${entry.teamId}:${entry.keeperPlayerIds.join(",")}`)
    .join("\u001e") ?? "";
  const teamNameSignature = snapshot?.presentTeams
    .map((team) => `${team.id}:${team.name}`)
    .join("\u001e") ?? "";

  const load = useCallback(async () => {
    await draftLoadCoordinator.current.run(
      () => api<DraftSnapshotView>("/api/draft"),
      {
        success(next) { setSnapshot(next); setUnavailable(false); },
        failure(error) {
          if (error instanceof ApiError && [404, 409].includes(error.status)) setUnavailable(true);
          else notify(error instanceof Error ? error.message : "Unable to load Keeper Draft.", "error");
        },
      },
    );
  }, [notify]);

  useEffect(() => {
    void load();
    const polling = window.setInterval(() => void load(), 6_000);
    const heartbeat = window.setInterval(() => void api("/api/draft/presence", { method: "POST", body: "{}" }).catch(() => undefined), 15_000);
    void api("/api/draft/presence", { method: "POST", body: "{}" }).catch(() => undefined);
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const realtime = createReconnectingDraftSocket({
      url: `${protocol}//${window.location.host}/ws/draft`,
      onMessage: () => void load(),
    });
    return () => {
      window.clearInterval(polling);
      window.clearInterval(heartbeat);
      realtime.dispose();
      void api("/api/draft/presence/disconnect", { method: "POST", body: "{}" }).catch(() => undefined);
    };
  }, [load]);

  useEffect(() => {
    const nextSoundSession = createKeeperDraftSoundSession(session.team?.id ?? null);
    soundSession.current = nextSoundSession;
    const primeSound = () => nextSoundSession.prime();
    document.addEventListener("pointerdown", primeSound, { once: true, passive: true });
    return () => {
      document.removeEventListener("pointerdown", primeSound);
      nextSoundSession.reset();
      nextSoundSession.dispose();
      if (soundSession.current === nextSoundSession) soundSession.current = null;
    };
  }, [session.team?.id]);

  useEffect(() => {
    if (snapshot) soundSession.current?.update(snapshot);
  }, [session.team?.id, snapshot]);

  useEffect(() => {
    const request = fitRequestSequence.current + 1;
    fitRequestSequence.current = request;
    if (!snapshot || !session.team?.name || snapshot.keeperLedgerReady !== true || snapshot.keeperLedger.length !== 12) {
      setFitView(null);
      return;
    }
    const nameById = new Map(snapshot.presentTeams.map((team) => [team.id, team.name]));
    const teamRosters = snapshot.keeperLedger.map((entry) => ({
      teamName: nameById.get(entry.teamId) ?? "",
      keeperPlayerIds: entry.keeperPlayerIds,
    }));
    if (teamRosters.some((roster) => !roster.teamName) || new Set(teamRosters.map((roster) => roster.teamName)).size !== 12) {
      setFitView(null);
      return;
    }
    setFitView(null);
    void draftLabKeeperDraftFits(
      session.team.name,
      keeperEligibleAvailablePlayers.map((player) => player.id),
      teamRosters,
    ).then((result) => {
      if (fitRequestSequence.current !== request) return;
      setFitView({
        rankedPlayerIds: result.rankedPlayerIds,
        roundedFitByPlayerId: Object.fromEntries(result.rows.map((row) => [row.playerId, row.roundedFit])),
      });
    }).catch(() => {
      if (fitRequestSequence.current === request) setFitView(null);
    });
  }, [availableFitSignature, keeperLedgerSignature, session.team?.name, teamNameSignature]);

  const latestChatMessageId = snapshot?.chat.length ? snapshot.chat[snapshot.chat.length - 1]?.id : undefined;
  useEffect(() => { scrollContainerToBottom(chatMessages.current); }, [latestChatMessageId]);

  async function prepare(playerId: string) {
    if (!snapshot) return;
    setBusy(true);
    try {
      const result = await api<PickPreparation>("/api/draft/pick", { method: "POST", body: JSON.stringify({ action: "prepare", playerId, expectedVersion: snapshot.version, expectedPickNumber: snapshot.currentPickNumber }) });
      setPreparation(result);
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to prepare pick.", "error"); await load(); }
    finally { setBusy(false); }
  }

  async function confirmPick() {
    if (!snapshot || !preparation) return;
    setBusy(true);
    try {
      await api("/api/draft/pick", { method: "POST", body: JSON.stringify({ action: "confirm", playerId: preparation.summary.playerId, confirmationToken: preparation.confirmationToken, idempotencyKey: createClientId(), expectedVersion: preparation.expectedVersion, expectedPickNumber: preparation.expectedPickNumber }) });
      notify(`${preparation.summary.playerName} is locked in.`);
      setPreparation(null); await load();
    } catch (error) { notify(error instanceof Error ? error.message : "Unable to confirm pick.", "error"); setPreparation(null); await load(); }
    finally { setBusy(false); }
  }

  async function sendChat(event: FormEvent) {
    event.preventDefault();
    const message = chat.trim();
    if (!message) return;
    setChat("");
    try { await api("/api/draft/chat", { method: "POST", body: JSON.stringify({ message }) }); await load(); }
    catch (error) { setChat(message); notify(error instanceof Error ? error.message : "Unable to send message.", "error"); }
  }

  function closeWithdrawalDialog() {
    if (busy) return;
    setConfirmingWithdrawal(false);
    setWithdrawChoiceRequired(false);
    setWithdrawOptions([]);
    setWithdrawOptionsError("");
    setWithdrawSearch("");
    setSpecialKeeperPlayerId("");
  }

  async function loadWithdrawOptions() {
    setWithdrawOptionsLoading(true);
    setWithdrawOptionsError("");
    try {
      const result = await api<WithdrawOptions>("/api/draft/withdraw-options");
      const players = Array.isArray(result.players)
        ? result.players.filter((player) => player && player.playerId && player.name && isKeeperEligibilityPlayer(player))
        : [];
      setWithdrawChoiceRequired(result.required);
      setWithdrawOptions(result.required ? players : []);
      if (!result.required) setSpecialKeeperPlayerId("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load the keeper catalog.";
      setWithdrawOptionsError(message);
      notify(message, "error");
    } finally {
      setWithdrawOptionsLoading(false);
    }
  }

  function beginWithdrawal(specialKeeperRequired: boolean) {
    setWithdrawChoiceRequired(specialKeeperRequired);
    setWithdrawOptions([]);
    setWithdrawOptionsError("");
    setWithdrawSearch("");
    setSpecialKeeperPlayerId("");
    setConfirmingWithdrawal(true);
    if (specialKeeperRequired) void loadWithdrawOptions();
  }

  async function withdraw(specialKeeperId?: string) {
    setBusy(true);
    try {
      const result = await api<{ withdrawn: boolean; specialKeeper: WithdrawalKeeperOption | null }>("/api/draft/withdraw", {
        method: "POST",
        body: JSON.stringify({
          idempotencyKey: createClientId(),
          ...(specialKeeperId ? { specialKeeperPlayerId: specialKeeperId } : {}),
        }),
      });
      setConfirmingWithdrawal(false);
      if (result.specialKeeper) {
        notify(`${result.specialKeeper.name} was granted eligibility, locked in as your keeper, and your team withdrew.`);
      } else {
        notify("Your team has withdrawn. Existing Keeper Draft selections remain locked.");
      }
      await load();
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to withdraw from the Keeper Draft.";
      notify(message, "error");
      if (error instanceof ApiError && error.code === "zero_keeper_choice_required") {
        setWithdrawChoiceRequired(true);
        await loadWithdrawOptions();
      } else if (error instanceof ApiError && error.code === "special_keeper_unavailable") {
        setSpecialKeeperPlayerId("");
        await loadWithdrawOptions();
      }
    } finally {
      setBusy(false);
    }
  }

  if (unavailable) return <DraftUnavailable summary={summary} commissioner={session.membership.role === "commissioner"} />;
  if (!snapshot) return <LoadingScreen label="Joining the Keeper Draft room…" />;
  if (snapshot.status === "completed") return <DraftResults snapshot={snapshot} summary={summary} onReturn={onReturn} />;
  const isMyTurn = Boolean(session.team && snapshot.currentTeamId === session.team.id);
  const availablePlayerById = new Map(keeperEligibleAvailablePlayers.map((player) => [player.id, player]));
  const fitCoversAvailablePool = Boolean(
    fitView
    && fitView.rankedPlayerIds.length === keeperEligibleAvailablePlayers.length
    && fitView.rankedPlayerIds.every((playerId) => availablePlayerById.has(playerId)),
  );
  const fitOrderedAvailablePlayers = fitCoversAvailablePool && fitView
    ? fitView.rankedPlayerIds.map((playerId) => availablePlayerById.get(playerId)!)
    : keeperEligibleAvailablePlayers;
  const candidates = fitOrderedAvailablePlayers.filter((player) => {
    const query = search.trim().toLocaleLowerCase();
    return (!query || `${player.name} ${player.nflTeam} ${player.positions.join(" ")}`.toLocaleLowerCase().includes(query))
      && matchesPositionFilter(player.positions, position);
  });
  const orderedTeams = orderDraftTeamStatuses(snapshot.teams, snapshot.currentTeamId);
  const authoritativeTurnOrder = (snapshot as DraftSnapshotView & { turnOrder?: DraftTurnOrderEntry[] }).turnOrder;
  const turnOrder = authoritativeTurnOrder?.length ? authoritativeTurnOrder : orderedTeams.map((team, index): DraftTurnOrderEntry => ({
    planPosition: index + 1,
    teamId: team.id,
    teamName: team.name,
    status: team.id === snapshot.currentTeamId ? "on_clock" : team.withdrawn ? "completed" : "pending",
    pickNumber: team.id === snapshot.currentTeamId ? snapshot.currentPickNumber : null,
  }));
  const viewerTeam = session.team ? snapshot.teams.find((team) => team.id === session.team?.id) : undefined;
  const viewerKeepers = [
    ...summary.keeperSelections.map((player) => ({ id: `initial-${player.playerId}`, name: player.name, detail: `${player.positions.join("/")} · ${player.nflTeam}` })),
    ...snapshot.picks.filter((pick) => pick.teamId === session.team?.id).map((pick) => ({ id: pick.id, name: pick.playerName, detail: `${pick.positions?.join("/") || "Position unavailable"} · ${pick.nflTeam ?? "Team unavailable"} · Keeper Draft pick #${pick.pickNumber}` })),
  ];
  const canWithdraw = Boolean(viewerTeam && !viewerTeam.withdrawn && viewerTeam.currentKeepers < snapshot.maxKeepers);
  const zeroKeeperWithdrawal = Boolean(
    viewerTeam
    && viewerTeam.initialKeepers === 0
    && viewerTeam.currentKeepers === 0
    && viewerKeepers.length === 0,
  );
  const withdrawQuery = withdrawSearch.trim().toLocaleLowerCase("en-US");
  const matchingWithdrawOptions = withdrawOptions.filter((player) => !withdrawQuery
    || `${player.name} ${player.nflTeam} ${player.positions.join(" ")}`.toLocaleLowerCase("en-US").includes(withdrawQuery));
  const visibleWithdrawOptions = matchingWithdrawOptions.slice(0, 60);
  const selectedSpecialKeeper = withdrawOptions.find((player) => player.playerId === specialKeeperPlayerId);
  return (
    <div className={`page keeper-draft-page ${snapshot.status === "lobby" ? "is-lobby" : "is-active"}`}>
      <section className={`draft-status-bar ${isMyTurn ? "is-your-turn" : ""}`}>
        <div><p className="eyebrow">{snapshot.status === "lobby" ? "Keeper Draft lobby" : isMyTurn ? "Your turn" : "On the clock"}</p><div className="on-clock-team">{snapshot.status === "lobby" ? "Waiting for the commissioner to start" : isMyTurn ? `${session.team?.name} — choose a keeper` : `${snapshot.currentTeamName ?? "Next team"} is choosing…`}</div></div>
        {snapshot.currentPickNumber > 0 && <div className="draft-clock" aria-label={`Pick ${snapshot.currentPickNumber}`}>#{snapshot.currentPickNumber}</div>}
      </section>
      <div className="draft-layout">
        <aside className="draft-left-column stack">
          <section className="draft-priority surface-card">
            <div className="card-header"><h2>Turn Order</h2><span className="status-pill status-pill--info">Turns: {turnOrder.length}</span></div>
            <ol className="priority-list turn-order-list">{turnOrder.map((turn) => <li key={`${turn.planPosition}-${turn.teamId}`} className={`priority-item ${turn.status === "on_clock" ? "is-on-clock" : ""} ${turn.teamId === session.team?.id ? "is-user-team" : ""} ${turn.status === "completed" ? "is-complete" : ""}`} aria-current={turn.status === "on_clock" ? "step" : undefined}><span className="priority-marker" aria-hidden="true">{String(turn.planPosition).padStart(2, "0")}</span><span className="priority-team">{turn.teamName}</span><span className={`status-pill ${turn.status === "completed" ? "status-pill--success" : turn.status === "on_clock" ? "status-pill--pending" : ""}`}>{turn.status === "completed" ? "Selected" : turn.status === "on_clock" ? "On Clock" : "Upcoming"}</span></li>)}</ol>
          </section>
          {session.team && <section className="viewer-keepers-panel surface-card">
            <div className="card-header"><h2>Your keepers</h2><span className="status-pill status-pill--info">Keepers: {viewerKeepers.length}/{snapshot.maxKeepers}</span></div>
            <ul className="viewer-keeper-list">{viewerKeepers.length === 0 ? <li className="viewer-keeper-empty">No keepers selected yet.</li> : viewerKeepers.map((keeper) => <li key={keeper.id}><strong>{keeper.name}</strong><span>{keeper.detail}</span></li>)}</ul>
            {canWithdraw && <div className="draft-panel-actions"><button className="button button--danger button--wide" type="button" disabled={busy} onClick={() => beginWithdrawal(zeroKeeperWithdrawal)}>Withdraw from Keeper Draft</button></div>}
          </section>}
        </aside>
        <main className="draft-main surface-card">
          <div className="card-header"><h2>Available eligible players</h2><span className="status-pill status-pill--info">Available: {candidates.length}</span></div>
          <div className="draft-main-toolbar"><label className="search-field"><span className="sr-only">Search players</span><input type="search" placeholder="Search player or NFL team" value={search} onChange={(event) => setSearch(event.target.value)} /></label><label className="position-filter"><span className="sr-only">Position</span><select value={position} onChange={(event) => setPosition(event.target.value)}><option value="ALL">All positions</option>{POSITION_FILTER_ORDER.map((value) => <option key={value} value={value}>{value}</option>)}</select></label></div>
          {snapshot.status === "lobby" ? <div className="draft-waiting"><div className="waiting-pulse" aria-hidden="true"><span /><span /><span /></div><h2>Lobby open</h2><p>Managers can join and chat while the commissioner prepares the draft.</p></div> : <div className="player-grid scroll-region">{candidates.map((player) => { const primaryPosition = player.positions[0] ?? "—"; const roundedFit = fitCoversAvailablePool ? fitView?.roundedFitByPlayerId[player.id] : undefined; return <article className="player-card" key={player.id}><div className="player-card-header"><div><h3 className="player-name">{player.name}</h3><p className="player-subtitle">{player.nflTeam} · {player.positions.join("/")}</p></div><span className="position-badge" data-position={primaryPosition}>{primaryPosition}</span></div><div className="player-metrics"><span className="compact-metric"><small className="compact-metric-label">{roundedFit === undefined ? "TB Proj" : "Fit"}</small><strong className="compact-metric-value">{roundedFit ?? player.teamBeerProjection ?? "—"}</strong></span><span className="compact-metric"><small className="compact-metric-label">Bye</small><strong className="compact-metric-value">{player.byeWeek ?? "—"}</strong></span><span className="compact-metric"><small className="compact-metric-label">Team</small><strong className="compact-metric-value">{player.nflTeam}</strong></span></div><div className="player-card-actions"><button className="button button--primary button--small" type="button" disabled={!isMyTurn || busy} onClick={() => void prepare(player.id)}>{isMyTurn ? "Select" : viewerTeam?.withdrawn || viewerTeam?.currentKeepers === snapshot.maxKeepers ? "Spectating" : "Waiting"}</button></div></article>; })}</div>}
        </main>
        <aside className="draft-sidebar stack">
          <section className="presence-panel surface-card"><div className="card-header"><h2>Managers</h2><span className="status-pill status-pill--success">Online: {snapshot.presentTeams.filter((team) => team.online).length}</span></div><ul className="presence-list">{snapshot.presentTeams.map((team) => <li key={team.id} className={`presence-item ${team.online ? "is-online" : ""}`}><span className="presence-dot" aria-hidden="true" /><span className="presence-name">{team.name}</span><span className="presence-state">{team.online ? "Here" : "Away"}</span></li>)}</ul></section>
          <section className="chat-panel surface-card"><div className="card-header"><h2>League chat</h2></div><div ref={chatMessages} className="chat-messages" aria-live="polite">{snapshot.chat.length === 0 && <p className="muted">No messages yet.</p>}{snapshot.chat.map((message) => <div key={message.id} className={`chat-message ${message.userId === session.user.id ? "is-own" : ""}`}><div className="chat-message-header"><span className="chat-author">{message.authorName}</span><time className="chat-time">{shortTime(message.createdAt)}</time></div><p>{message.message}</p></div>)}</div><form className="chat-form" onSubmit={(event) => void sendChat(event)}><input aria-label="Chat message" maxLength={500} placeholder="Message the league" value={chat} onChange={(event) => setChat(event.target.value)} /><button className="button button--secondary button--small" type="submit" disabled={!chat.trim()}>Send</button></form></section>
        </aside>
      </div>
      <ConfirmDialog open={Boolean(preparation)} title="Lock in this keeper?" confirmLabel="Confirm selection" busy={busy} onCancel={() => setPreparation(null)} onConfirm={() => void confirmPick()}>
        {preparation && <div className="pick-confirmation-content"><div className="confirmation-player"><span className="position-badge">✓</span><div><strong>{preparation.summary.playerName}</strong><p>{preparation.summary.teamName}</p></div></div><p className="pick-confirmation-note">This selection is final and immediately advances the Keeper Draft.</p></div>}
      </ConfirmDialog>
      <ConfirmDialog open={confirmingWithdrawal} title={withdrawChoiceRequired ? "Choose your one keeper and withdraw?" : "Withdraw from the Keeper Draft?"} confirmLabel={withdrawChoiceRequired ? "Lock keeper and withdraw" : "Withdraw team"} danger busy={busy} confirmDisabled={withdrawChoiceRequired && (withdrawOptionsLoading || Boolean(withdrawOptionsError) || !specialKeeperPlayerId)} onCancel={closeWithdrawalDialog} onConfirm={() => void withdraw(withdrawChoiceRequired ? specialKeeperPlayerId : undefined)}>
        {withdrawChoiceRequired ? <div className="withdrawal-confirmation special-keeper-withdrawal">
          <div className="alert alert--warning"><span aria-hidden="true">!</span><span>Because your team submitted zero keepers and selected zero in the Keeper Draft, Team Beer rules grant exactly one keeper before withdrawal.</span></div>
          <p>Choose any available individual player. The player may already be Keeper Draft eligible or can be granted eligibility now, but cannot already be on another team’s final keeper roster. Team DEF/ST units are excluded.</p>
          <label className="field"><span>Search the full player catalog</span><input type="search" autoComplete="off" placeholder="Player, NFL team, or position" value={withdrawSearch} onChange={(event) => setWithdrawSearch(event.target.value)} /></label>
          {withdrawOptionsLoading ? <div className="withdraw-option-state" role="status"><ButtonSpinner />Loading available players…</div> : withdrawOptionsError ? <div className="alert alert--danger" role="alert">{withdrawOptionsError}</div> : <>
            <div className="withdraw-option-summary"><span>{matchingWithdrawOptions.length} available player{matchingWithdrawOptions.length === 1 ? "" : "s"}</span>{matchingWithdrawOptions.length > visibleWithdrawOptions.length && <span>Showing the first {visibleWithdrawOptions.length}; refine your search.</span>}</div>
            <div className="withdraw-option-list" role="radiogroup" aria-label="Choose the keeper granted before withdrawal">
              {visibleWithdrawOptions.map((player) => <label className={`withdraw-option ${specialKeeperPlayerId === player.playerId ? "is-selected" : ""}`} key={player.playerId}>
                <input type="radio" name="specialKeeperPlayerId" value={player.playerId} checked={specialKeeperPlayerId === player.playerId} onChange={() => setSpecialKeeperPlayerId(player.playerId)} />
                <span className="withdraw-option-copy"><strong>{player.name}</strong><small>{player.positions.join("/")} · {player.nflTeam}{player.byeWeek ? ` · Bye ${player.byeWeek}` : ""}</small></span>
                <span className={`status-pill ${player.keeperDraftEligible ? "status-pill--info" : "status-pill--pending"}`}>{player.keeperDraftEligible ? "Eligible" : "Grant Eligibility"}</span>
              </label>)}
              {matchingWithdrawOptions.length === 0 && <p className="muted withdraw-option-empty">No available individual players match that search.</p>}
            </div>
          </>}
          {selectedSpecialKeeper && <div className="special-keeper-selection" role="status"><span>Selected keeper</span><strong>{selectedSpecialKeeper.name} · {selectedSpecialKeeper.positions.join("/")} · {selectedSpecialKeeper.nflTeam}</strong></div>}
          <p>Your selected keeper will be final. Your team will then be removed from every remaining turn, while you can continue to spectate and use league chat.</p>
        </div> : <div className="withdrawal-confirmation"><p>Your completed selections will remain locked and persistent.</p><p>Your team will be removed from every remaining turn in the draft order. You can continue to spectate and use league chat.</p></div>}
      </ConfirmDialog>
    </div>
  );
}

function DraftUnavailable({ summary, commissioner }: { summary: LeagueSummary; commissioner: boolean }) {
  return (
    <div className="page page--narrow"><section className="hero-card"><p className="eyebrow">Keeper Draft</p><h1>The lobby is not open yet</h1><p>{summary.teamsRemaining > 0 ? `${summary.teamsRemaining} ${summary.teamsRemaining === 1 ? "team is" : "teams are"} still choosing keepers.` : commissioner ? "All teams submitted. Initialize the lobby from League Settings when you are ready." : "The commissioner will open the lobby shortly."}</p></section></div>
  );
}

function DraftResults({ snapshot, summary, onReturn }: {
  snapshot: DraftSnapshotView;
  summary: LeagueSummary;
  onReturn: () => void;
}) {
  const grouped = snapshot.teams.map((team) => ({ team, picks: snapshot.picks.filter((pick) => pick.teamId === team.id) })).filter((item) => item.picks.length > 0 || item.team.withdrawn);
  return (
    <div className="page">
      <div className="page-heading"><div><p className="eyebrow">Keeper Draft complete</p><h1>Keeper Draft Results</h1><p>The next step is the Standard Draft on Sleeper {summary.standardDraftAt ? `on ${readableDate(summary.standardDraftAt)}` : "at a date the commissioner will announce"}.</p></div><div className="page-heading-actions"><button className="button button--secondary" type="button" onClick={onReturn}>Return to team page</button><a className="button button--secondary" href={summary.sleeperLeagueUrl} target="_blank" rel="noreferrer">Open Sleeper league ↗</a>{summary.draftLabHandoffAvailable && summary.draftLabHandoffUrl ? <a className="button button--primary" href={summary.draftLabHandoffUrl} target="_blank" rel="noreferrer">Continue in Draft Lab ↗</a> : null}</div></div>
      <section className="surface-card"><div className="card-header"><h2>Results by team</h2><span className="status-pill status-pill--success">Selections: {snapshot.picks.length}</span></div><div className="card-body results-list">{grouped.map(({ team, picks }) => <div className="result-row" key={team.id}><strong className="result-team-name">{team.name}</strong><span className="result-player-list">{picks.length > 0 ? picks.map((pick) => pick.playerName).join(", ") : "No Keeper Draft selections"}</span><span className="status-pill status-pill--success result-count-pill">Keepers: {team.currentKeepers}/{snapshot.maxKeepers}</span></div>)}</div></section>
    </div>
  );
}

function Toasts({ messages }: { messages: ToastMessage[] }) {
  return <div className="toast-region" aria-live="polite" aria-atomic="true">{messages.map((toast) => <div key={toast.id} className={`toast toast--${toast.kind}`}><span aria-hidden="true">{toast.kind === "success" ? "✓" : "!"}</span><span>{toast.message}</span></div>)}</div>;
}

function App() {
  const verificationRequestActiveRef = useRef(false);
  const verificationAutoStartedRef = useRef(false);
  const [booting, setBooting] = useState(() => emailVerificationController.kind === "none");
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [summary, setSummary] = useState<LeagueSummary | null>(null);
  const [page, setPage] = useState<Page>(initialPageFromLocation);
  const [authView, setAuthView] = useState<AuthView>(() => {
    if (emailVerificationController.kind !== "none") return "verify";
    const path = window.location.pathname.toLocaleLowerCase();
    if (path.includes("reset-password")) return "reset";
    return "login";
  });
  const [verificationIdentifier, setVerificationIdentifier] = useState("");
  const [verificationState, setVerificationState] = useState<EmailVerificationState>(() => {
    if (emailVerificationController.kind === "pending") return "confirming";
    if (emailVerificationController.kind === "invalid" || emailVerificationController.kind === "missing") return "invalid";
    return "inbox";
  });
  const [verificationRetryAvailableAt, setVerificationRetryAvailableAt] = useState<number | null>(null);
  const [verificationSucceeded, setVerificationSucceeded] = useState(false);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [keeperSelectionDirty, setKeeperSelectionDirty] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<Page | null>(null);
  const [pollDialogOpen, setPollDialogOpen] = useState(false);
  const pollController = useStandardDraftTimePoll(Boolean(session), api);

  const notify = useCallback((message: string, kind: ToastMessage["kind"] = "success") => {
    const id = createClientId();
    setToasts((current) => [...current, { id, message, kind }]);
    window.setTimeout(() => setToasts((current) => current.filter((toast) => toast.id !== id)), 4_800);
  }, []);

  const loadSummary = useCallback(async () => {
    const next = await api<LeagueSummary>("/api/league/summary");
    setSummary(next);
  }, []);

  const loadSession = useCallback(async () => {
    try {
      const next = await api<SessionInfo>("/api/me");
      setSession(next);
      await loadSummary();
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 401) notify(error instanceof Error ? error.message : "Unable to open Team Beer.", "error");
      setSession(null); setSummary(null);
    } finally { setBooting(false); }
  }, [loadSummary, notify]);

  const confirmCapturedEmailVerification = useCallback(async () => {
    if (verificationRequestActiveRef.current) return;
    if (emailVerificationController.kind !== "pending") return;
    verificationRequestActiveRef.current = true;
    setVerificationState("confirming");
    setVerificationRetryAvailableAt(null);
    try {
      await emailVerificationController.confirm();
      setVerificationState("inbox");
      setVerificationRetryAvailableAt(null);
      setVerificationSucceeded(true);
      setAuthView("login");
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && error.code === "invalid_verification_token") {
        emailVerificationController.clear();
        setVerificationRetryAvailableAt(null);
        setVerificationState("invalid");
      } else {
        const retryAfter = error instanceof ApiError && error.status === 429
          ? error.retryAfterSeconds
          : null;
        const boundedRetryAfter = retryAfter === null
          ? 0
          : Math.min(3_600, Math.max(0, retryAfter));
        setVerificationRetryAvailableAt(
          boundedRetryAfter > 0 ? Date.now() + (boundedRetryAfter * 1_000) : null,
        );
        setVerificationState("transient");
      }
    } finally {
      verificationRequestActiveRef.current = false;
    }
  }, []);

  const navigate = useCallback(async (nextPage: Page, discardUnsaved = false) => {
    if (page === "keepers" && keeperSelectionDirty && nextPage !== "keepers" && !discardUnsaved) {
      setPendingNavigation(nextPage);
      return;
    }
    if (nextPage === "home") {
      try {
        await loadSummary();
      } catch (error) {
        notify(error instanceof Error ? error.message : "Unable to refresh the team page.", "error");
        return;
      }
    }
    setPage(nextPage);
  }, [keeperSelectionDirty, loadSummary, notify, page]);

  useEffect(() => {
    if (emailVerificationController.kind === "pending") {
      setBooting(false);
      if (!verificationAutoStartedRef.current) {
        verificationAutoStartedRef.current = true;
        void confirmCapturedEmailVerification();
      }
      return;
    }
    if (emailVerificationController.kind === "invalid" || emailVerificationController.kind === "missing") {
      setBooting(false);
      return;
    }
    void loadSession();
  }, [confirmCapturedEmailVerification, loadSession]);

  useEffect(() => {
    if (!session) return undefined;
    let active = true;
    let refreshInFlight = false;
    const refreshVisibleSummary = async () => {
      if (!active || refreshInFlight || document.visibilityState === "hidden") return;
      refreshInFlight = true;
      try {
        const next = await api<LeagueSummary>("/api/league/summary");
        if (active) setSummary(next);
      } catch {
        // Navigation and explicit actions still surface errors. Background
        // refresh stays quiet through a brief offline or expired-tab interval.
      } finally {
        refreshInFlight = false;
      }
    };
    const refreshOnVisibility = () => {
      if (document.visibilityState === "visible") void refreshVisibleSummary();
    };
    const summaryRefreshTimer = window.setInterval(() => {
      void refreshVisibleSummary();
    }, 15_000);
    window.addEventListener("focus", refreshOnVisibility);
    document.addEventListener("visibilitychange", refreshOnVisibility);
    return () => {
      active = false;
      window.clearInterval(summaryRefreshTimer);
      window.removeEventListener("focus", refreshOnVisibility);
      document.removeEventListener("visibilitychange", refreshOnVisibility);
    };
  }, [session]);

  useEffect(() => {
    if (page === "home"
      && session
      && summary?.keeperDraftRequired
      && ["keeper_draft_lobby", "keeper_draft_active"].includes(summary.phase)
      && !summary.keeperDraftComplete) {
      setPage("draft");
    }
  }, [page, session, summary]);

  async function logout() {
    try { await api("/api/auth/logout", { method: "POST", body: "{}" }); }
    catch { /* Locally clear the UI even if the session already expired. */ }
    returnBrowserHistoryToLeagueHub();
    emailVerificationController.clear();
    setVerificationState("inbox");
    setVerificationSucceeded(false);
    setSession(null); setSummary(null); setPage("home"); setAuthView("login"); setKeeperSelectionDirty(false); setPendingNavigation(null); setPollDialogOpen(false);
  }

  function openStandardDraftPoll() {
    if (page === "home") {
      const inlinePoll = document.getElementById("standard-draft-time-poll-inline");
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      inlinePoll?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "start" });
      window.requestAnimationFrame(() => inlinePoll?.querySelector<HTMLElement>("h2")?.focus());
      return;
    }
    setPollDialogOpen(true);
  }

  function closeStandardDraftPoll() {
    setPollDialogOpen(false);
    window.requestAnimationFrame(() => document.getElementById("standard-draft-time-poll-trigger")?.focus());
  }

  useEffect(() => {
    if (page === "home" && pollDialogOpen) setPollDialogOpen(false);
  }, [page, pollDialogOpen]);

  async function keeperDone() { setKeeperSelectionDirty(false); await navigate("home", true); }

  function discardKeeperChangesAndNavigate() {
    const destination = pendingNavigation ?? "home";
    setPendingNavigation(null);
    setKeeperSelectionDirty(false);
    void navigate(destination, true);
  }

  if (booting) return <LoadingScreen />;
  if (!session) return <><AuthShell
    view={authView}
    onView={(next) => {
      if (next !== "verify") {
        emailVerificationController.clear();
        setVerificationRetryAvailableAt(null);
        setVerificationState("inbox");
      }
      setAuthView(next);
    }}
    onAuthenticated={loadSession}
    onRegistered={(identifier) => {
      setVerificationIdentifier(identifier);
      emailVerificationController.clear();
      setVerificationRetryAvailableAt(null);
      setVerificationState("inbox");
      setVerificationSucceeded(false);
      setAuthView("verify");
    }}
    onLoginVerificationRequired={(identifier) => {
      setVerificationIdentifier(identifier);
      setVerificationRetryAvailableAt(null);
      setVerificationState("inbox");
      setVerificationSucceeded(false);
      setAuthView("verify");
    }}
    verificationIdentifier={verificationIdentifier}
    verificationState={verificationState}
    verificationRetryAvailableAt={verificationRetryAvailableAt}
    verificationSucceeded={verificationSucceeded}
    onRetryVerification={() => void confirmCapturedEmailVerification()}
    notify={notify}
  /><Toasts messages={toasts} /></>;
  if (!summary) return <LoadingScreen label="Loading league status…" />;
  const savedPollRanking = pollController.view?.ballot?.rankedOptionIds ?? [];
  const pollComplete = savedPollRanking.length === 10
    && pollController.ranking.length === 10
    && savedPollRanking.every((optionId, index) => optionId === pollController.ranking[index])
    && !["saving", "error", "conflict"].includes(pollController.saveState);
  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Header session={session} page={page} pollRankedCount={pollController.ranking.length} pollComplete={pollComplete} onOpenPoll={openStandardDraftPoll} onNavigate={(nextPage) => void navigate(nextPage)} onLogout={() => void logout()} />
      <main id="main-content" className="page-shell">
        {page === "home" && <Dashboard summary={summary} session={session} pollController={pollController} onNavigate={(nextPage) => void navigate(nextPage)} onRefresh={loadSummary} notify={notify} />}
        {page === "keepers" && <KeeperSelection notify={notify} onDone={keeperDone} onReturn={() => void navigate("home")} onDirtyChange={setKeeperSelectionDirty} />}
        {page === "rosters" && <RostersPage currentTeamId={session.team?.id ?? null} onReturn={() => void navigate("home")} />}
        {page === "settings" && session.membership.role === "commissioner" && <LeagueSettings notify={notify} onDraft={() => setPage("draft")} onReturn={() => void navigate("home")} onRefresh={loadSession} onPollReset={pollController.load} />}
        {page === "draft" && <KeeperDraft session={session} summary={summary} notify={notify} onReturn={() => void navigate("home")} />}
      </main>
      {page !== "home" && <StandardDraftTimePollDialog open={pollDialogOpen} controller={pollController} onClose={closeStandardDraftPoll} />}
      <ConfirmDialog open={pendingNavigation !== null} title="Leave without saving?" cancelLabel="Keep editing" confirmLabel="Leave without saving" danger onCancel={() => setPendingNavigation(null)} onConfirm={discardKeeperChangesAndNavigate}>
        <p>Your keeper selections have changed since the last save. Returning to the team page now will discard those unsaved changes.</p>
      </ConfirmDialog>
      <Toasts messages={toasts} />
    </div>
  );
}

const root = document.getElementById("root");
if (!root) throw new Error("Team Beer root element is missing.");
createRoot(root).render(<App />);
